import { useParams, Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { allPosts, featuredPost } from '../../mocks/blog-posts';
import FeedbackBanner from '../../components/feature/FeedbackBanner';
import { useEffect } from 'react';

export default function Article() {
  const { slug } = useParams();
  
  // Combinar todos os posts para buscar
  const allArticles = [featuredPost, ...allPosts];
  const article = allArticles.find(post => post.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!article) {
    return (
      <>
        <SEOHead 
          title="Artigo não encontrado - Ativo News"
          description="O artigo que você está procurando não existe."
          keywords="not found, article, error"
          canonical={`https://ativonews.com.br/artigo/${slug}`}
        />
        <SchemaOrg type="Article" data={{
          headline: "Artigo não encontrado",
          description: "O artigo que você está procurando não existe.",
          author: "Ativo News",
          datePublished: new Date().toISOString(),
          image: "https://ativonews.com.br/images/ogimage.jpg"
        }} />
        
        <div className="min-h-screen bg-white">
          <Navbar />
          <div className="max-w-7xl mx-auto px-6 py-20 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Artigo não encontrado</h1>
            <p className="text-gray-600 mb-8">O artigo que você está procurando não existe.</p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E40AF] text-white rounded-lg hover:bg-[#1E3A8A] transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar ao Blog
            </Link>
          </div>
          <FeedbackBanner 
            articleTitle="Artigo não encontrado"
            articleUrl={typeof window !== 'undefined' ? window.location.href : ''}
          />
          <Footer />
        </div>
      </>
    );
  }

  // Se for o artigo de benefícios corporativos, renderiza conteúdo específico
  if (slug === 'simples-nacional-vantagens-desvantagens-2025') {
    return (
      <>
        <SEOHead 
          title="Simples Nacional na Nova Era da Reforma Tributária: O Que Muda e Como se Preparar | Guia Técnico 2026"
          description="Análise técnica completa sobre o impacto da Reforma Tributária no Simples Nacional: crédito tributário, CBS, IBS, split payment, competitividade B2B e estratégias de adaptação para empresas."
          keywords="simples nacional, reforma tributária, CBS, IBS, crédito tributário, split payment, regime de tributação, lucro presumido, lucro real, competitividade empresarial, compliance fiscal"
          canonical={`https://ativonews.com.br/artigo/${slug}`}
        />
        <SchemaOrg type="Article" data={{
          headline: "Simples Nacional na Nova Era da Reforma Tributária: O Que Muda e Como se Preparar",
          description: "Análise técnica sobre os impactos da Reforma Tributária no Simples Nacional e estratégias para manter competitividade",
          author: "Dr. Paulo Mendes",
          datePublished: "2025-01-20",
          image: "https://public.readdy.ai/ai/img_res/2b38f6088baf9369441cf432153e0b66.jpg"
        }} />
        
        <div className="min-h-screen bg-white">
          <Navbar />
          
          {/* Hero Section */}
          <div className="w-full bg-gradient-to-b from-[#F8FAFC] to-white">
            <div className="max-w-7xl mx-auto px-6 pt-32 pb-16">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-[#64748B] mb-8">
                <Link to="/" className="hover:text-[#1E40AF] transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <Link to="/categoria/tributario" className="hover:text-[#1E40AF] transition-colors">
                  Tributário
                </Link>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <span className="text-[#0F172A] font-medium">Simples Nacional</span>
              </nav>

              {/* Category Badge */}
              <div className="mb-6">
                <span className="inline-block px-4 py-2 bg-[#DC2626] text-white text-sm font-bold rounded-lg uppercase tracking-wide">
                  Tributário
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight max-w-4xl">
                Simples Nacional na Nova Era da Reforma Tributária: O Que Muda e Como se Preparar
              </h1>

              {/* Excerpt */}
              <p className="text-xl text-[#475569] mb-8 leading-relaxed max-w-3xl">
                Análise técnica completa sobre os impactos da Reforma Tributária no Simples Nacional: crédito tributário, CBS, IBS, competitividade B2B e estratégias essenciais para empresas.
              </p>

              {/* Author & Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-[#64748B]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-gray-400"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#0F172A]">Dr. Paulo Mendes</div>
                    <div className="text-sm">Especialista em Planejamento Tributário</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  <span>20 de janeiro de 2025</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  <span>18 min</span>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full h-[500px] bg-gray-100">
            <img 
              src="https://readdy.ai/api/search-image?query=modern%20business%20meeting%20professional%20office%20financial%20planning%20tax%20reform%20strategy%20session%20business%20people%20analyzing%20documents%20charts%20graphs%20collaborative%20workspace%20brazil%20economic%20environment%20contemporary%20design%20natural%20lighting&width=1200&height=500&seq=simples-nacional-featured&orientation=landscape"
              alt="Simples Nacional e Reforma Tributária"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content Area */}
          <div className="w-full bg-white py-16">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-12 gap-12">
                
                {/* Main Content */}
                <article className="lg:col-span-8">
                  <div className="prose prose-lg max-w-none">
                    
                    {/* TL;DR */}
                    <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-10 rounded-r-lg">
                      <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-flashlight-line text-red-600"></i>
                        TL;DR — Resumo Executivo
                      </h2>
                      <p className="text-gray-700 leading-relaxed">
                        O Simples Nacional foi preservado constitucionalmente pela Reforma Tributária, mas o ambiente ao redor mudou completamente. <strong>O principal impacto é o crédito tributário:</strong> empresas fora do Simples preferirão fornecedores que gerem crédito de CBS e IBS, criando desvantagem competitiva para optantes do regime simplificado, especialmente em operações B2B. Outros desafios incluem split payment (redução de fluxo de caixa), maior complexidade operacional e necessidade de revisão estratégica do modelo de negócio. Este guia apresenta análise técnica dos impactos, cronograma de transição (2026-2033) e estratégias práticas para manter competitividade.
                      </p>
                    </div>

                    {/* Introdução */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">Introdução</h2>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        A Reforma Tributária brasileira inaugura uma nova era no sistema de arrecadação, trazendo profundas mudanças na forma como os tributos sobre o consumo serão cobrados. Embora o Simples Nacional tenha sido preservado constitucionalmente, na prática o impacto será significativo, exigindo atenção redobrada de empresários, contadores e gestores.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        Neste artigo, você vai entender como o Simples Nacional se posiciona diante da Reforma Tributária, quais são os riscos, oportunidades, pontos de atenção e estratégias essenciais para manter competitividade e conformidade nos próximos anos.
                      </p>
                    </section>

                    {/* Seção 1 */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">O Simples Nacional Continua Existindo?</h2>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        <strong>Sim.</strong> O Simples Nacional foi mantido pela Reforma Tributária, conforme previsto na Constituição. No entanto, <strong>manter não significa permanecer igual</strong>.
                      </p>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        O grande desafio está no novo ambiente tributário, onde surgem dois novos tributos principais:
                      </p>
                      <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-government-line text-blue-600"></i>
                            CBS (Federal)
                          </h3>
                          <p className="text-sm text-gray-700">Contribuição sobre Bens e Serviços — substitui PIS e COFINS</p>
                        </div>
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-5">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-community-line text-green-600"></i>
                            IBS (Estadual/Municipal)
                          </h3>
                          <p className="text-sm text-gray-700">Imposto sobre Bens e Serviços — substitui ICMS e ISS</p>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        Esses tributos substituem gradualmente impostos como PIS, Cofins, ICMS, ISS e IPI, criando um sistema baseado em <strong>IVA (Imposto sobre Valor Agregado)</strong>.
                      </p>
                    </section>

                    {/* Seção 2 - Principal Impacto */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">O Principal Impacto: Crédito Tributário</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        O ponto mais crítico para empresas do Simples Nacional na nova Reforma Tributária é o <strong>direito ao crédito tributário</strong>.
                      </p>

                      <div className="space-y-6">
                        <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <i className="ri-history-line text-gray-600"></i>
                            Como era antes:
                          </h3>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-gray-400 text-xs mt-1.5"></i>
                              <span>Empresas do Simples não geravam crédito para seus clientes</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-gray-400 text-xs mt-1.5"></i>
                              <span>Isso raramente era um problema em cadeias simples ou B2C</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-gray-400 text-xs mt-1.5"></i>
                              <span>A carga tributária reduzida compensava a ausência de crédito</span>
                            </li>
                          </ul>
                        </div>

                        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <i className="ri-alert-line text-red-600"></i>
                            Como será agora:
                          </h3>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-close-circle-fill text-red-600 text-xs mt-1.5"></i>
                              <span>Empresas fora do Simples <strong>preferem fornecedores que geram crédito</strong> de CBS e IBS</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-close-circle-fill text-red-600 text-xs mt-1.5"></i>
                              <span>Empresas do Simples <strong>podem se tornar menos atrativas comercialmente</strong></span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-close-circle-fill text-red-600 text-xs mt-1.5"></i>
                              <span>Risco de <strong>perda de mercado em operações B2B</strong></span>
                            </li>
                          </ul>
                        </div>

                        <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
                          <p className="text-gray-900 font-bold mb-2 flex items-center gap-2">
                            <i className="ri-arrow-right-circle-fill text-orange-600"></i>
                            Resultado prático:
                          </p>
                          <p className="text-gray-700">
                            <strong>Perda de competitividade</strong>, especialmente em operações B2B. Empresas podem pagar menos imposto, mas vender menos por não gerar crédito para o comprador.
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* Seção 3 - Novo Dilema */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Simples Nacional x Regime Regular: O Novo Dilema</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        Com a Reforma Tributária, surge uma pergunta estratégica:
                      </p>

                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-8 mb-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                          Ainda vale a pena permanecer no Simples Nacional?
                        </h3>
                        <p className="text-center text-gray-700 mb-6">
                          A resposta passa por fatores como:
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-white p-4 rounded-lg">
                            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-user-line text-purple-600"></i>
                              Tipo de cliente
                            </h4>
                            <p className="text-sm text-gray-700">B2B ou B2C? Empresas B2B sofrem mais com falta de crédito</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-percent-line text-purple-600"></i>
                              Margem de lucro
                            </h4>
                            <p className="text-sm text-gray-700">Margens apertadas exigem análise precisa de custo tributário</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-links-line text-purple-600"></i>
                              Cadeia de crédito
                            </h4>
                            <p className="text-sm text-gray-700">Posição na cadeia produtiva influencia valor do crédito</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-money-dollar-circle-line text-purple-600"></i>
                              Repasse de preço
                            </h4>
                            <p className="text-sm text-gray-700">Capacidade de repassar custos ao cliente final</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-file-list-line text-purple-600"></i>
                              Complexidade operacional
                            </h4>
                            <p className="text-sm text-gray-700">Capacidade de gestão fiscal mais complexa</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-line-chart-line text-purple-600"></i>
                              Perspectiva de crescimento
                            </h4>
                            <p className="text-sm text-gray-700">Planos de expansão e faturamento futuro</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
                        <p className="text-gray-900 font-bold mb-2">⚠️ Alerta Estratégico:</p>
                        <p className="text-gray-700">
                          Em muitos casos, empresas do Simples poderão <strong>pagar menos imposto, mas vender menos</strong> por não gerar crédito. O custo oculto pode superar a economia tributária aparente.
                        </p>
                      </div>
                    </section>

                    {/* Seção 4 - Split Payment */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Split Payment e o Simples Nacional</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        Outro ponto sensível é o <strong>Split Payment</strong>, mecanismo pelo qual o imposto pode ser recolhido no momento da transação, diretamente ao fisco.
                      </p>

                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <i className="ri-wallet-line text-blue-600"></i>
                          O que é Split Payment?
                        </h3>
                        <p className="text-gray-700 mb-4">
                          Sistema em que o tributo é <strong>retido automaticamente no momento do pagamento</strong>, antes mesmo de cair na conta da empresa. Funciona como uma "separação" do valor da transação:
                        </p>
                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm text-gray-700 font-mono">
                            Venda de R$ 100,00<br />
                            → R$ 85,00 vai para a empresa<br />
                            → R$ 15,00 vai direto para o Fisco
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                          <h4 className="font-bold text-gray-900 mb-2">❌ Impacto negativo 1: Redução de fluxo de caixa</h4>
                          <p className="text-sm text-gray-700">Empresa não tem mais acesso ao valor total da venda, dificultando capital de giro</p>
                        </div>
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                          <h4 className="font-bold text-gray-900 mb-2">❌ Impacto negativo 2: Menor controle sobre capital</h4>
                          <p className="text-sm text-gray-700">Perda da capacidade de "usar" o tributo temporariamente antes do recolhimento</p>
                        </div>
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                          <h4 className="font-bold text-gray-900 mb-2">❌ Impacto negativo 3: Dependência de sistemas</h4>
                          <p className="text-sm text-gray-700">Exige automação e integração tecnológica para gestão eficiente</p>
                        </div>
                      </div>

                      <p className="text-gray-700 leading-relaxed mt-6">
                        <strong>Para empresas do Simples:</strong> Isso pode representar um impacto financeiro relevante, mesmo com carga tributária reduzida. Gestão de caixa se torna ainda mais crítica.
                      </p>
                    </section>

                    {/* Seção 5 - Fim da Simplicidade */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">O Fim da Simplicidade Operacional?</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        Apesar do nome, o Simples Nacional tende a se tornar <strong>menos simples</strong>.
                      </p>

                      <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-300 rounded-xl p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">A nova era exige:</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-white p-4 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-database-line text-orange-600 text-xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Controle detalhado de receitas</h4>
                                <p className="text-sm text-gray-700">Separação por tipo de operação, produto, serviço</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-price-tag-line text-orange-600 text-xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Classificação correta</h4>
                                <p className="text-sm text-gray-700">NCM, CEST, natureza da operação</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-split-cells-horizontal text-orange-600 text-xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Separação clara</h4>
                                <p className="text-sm text-gray-700">Distinção entre produtos e serviços</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-links-line text-orange-600 text-xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Integração com sistemas</h4>
                                <p className="text-sm text-gray-700">ERP atualizado e conforme exigências fiscais</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-search-eye-line text-orange-600 text-xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Auditoria contínua</h4>
                                <p className="text-sm text-gray-700">Monitoramento permanente de dados e conformidade</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-file-list-3-line text-orange-600 text-xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Documentação robusta</h4>
                                <p className="text-sm text-gray-700">Registros detalhados de todas as operações</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mt-6">
                        <p className="text-gray-900 font-bold mb-2">💡 Conclusão:</p>
                        <p className="text-gray-700">
                          A simplicidade será apenas <strong>no recolhimento</strong> (guia única), não mais na gestão. As exigências de controle e conformidade aumentam significativamente.
                        </p>
                      </div>
                    </section>

                    {/* Seção 6 - Cronograma */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Cronograma 2026–2033: Transição Gradual</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        A transição não acontece de uma vez. O cronograma oficial prevê implementação gradual:
                      </p>

                      <div className="space-y-4">
                        <div className="bg-white border-2 border-teal-300 rounded-xl p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-2xl font-bold">2026</span>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Fase de Testes e Adaptação</h3>
                              <p className="text-gray-700 mb-3">
                                Documentos fiscais devem destacar CBS/IBS, mas <strong>sem cobrança efetiva</strong>. Ano de preparação e ajustes.
                              </p>
                              <div className="bg-teal-50 p-3 rounded-lg">
                                <p className="text-sm font-semibold text-gray-900 mb-1">Ações necessárias:</p>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  <li>• Atualização de ERPs</li>
                                  <li>• Treinamento de equipes</li>
                                  <li>• Revisão de cadastros</li>
                                  <li>• Simulações tributárias</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white border-2 border-blue-300 rounded-xl p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-2xl font-bold">2027</span>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Início da CBS e Imposto Seletivo</h3>
                              <p className="text-gray-700 mb-3">
                                Cobrança efetiva de CBS (federal). PIS/COFINS começam a ser reduzidos gradualmente.
                              </p>
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <p className="text-sm font-semibold text-gray-900 mb-1">Impactos:</p>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  <li>• Cálculo real de CBS</li>
                                  <li>• Apuração de créditos</li>
                                  <li>• Ajustes de preços</li>
                                  <li>• Competitividade afetada</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white border-2 border-purple-300 rounded-xl p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-2xl font-bold">2029</span>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Início da Transição do IBS</h3>
                              <p className="text-gray-700 mb-3">
                                ICMS e ISS começam a ser reduzidos. <strong>Coexistência</strong> de tributos antigos e novos.
                              </p>
                              <div className="bg-purple-50 p-3 rounded-lg">
                                <p className="text-sm font-semibold text-gray-900 mb-1">Desafios:</p>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  <li>• Maior complexidade de apuração</li>
                                  <li>• Sistemas em duplicidade</li>
                                  <li>• Gestão de créditos mistos</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white border-2 border-green-300 rounded-xl p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 bg-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-2xl font-bold">2033</span>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Conclusão da Transição</h3>
                              <p className="text-gray-700 mb-3">
                                ICMS e ISS <strong>extintos</strong>. Apenas CBS, IBS e Imposto Seletivo em vigor.
                              </p>
                              <div className="bg-green-50 p-3 rounded-lg">
                                <p className="text-sm font-semibold text-gray-900 mb-1">Cenário final:</p>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  <li>• Sistema tributário simplificado</li>
                                  <li>• Conformidade total obrigatória</li>
                                  <li>• Fiscalização automatizada</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mt-6">
                        <p className="text-gray-900 font-bold mb-2">⚠️ Alerta Crítico:</p>
                        <p className="text-gray-700">
                          <strong>2026 é o ano decisivo para preparação.</strong> Quem não se adaptar enfrentará problemas operacionais graves a partir de 2027, quando a cobrança efetiva começar.
                        </p>
                      </div>
                    </section>

                    {/* Seção 7 - Estratégias */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Estratégias Essenciais para Empresas do Simples</h2>

                      <div className="space-y-6">
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <i className="ri-search-line text-purple-600"></i>
                            1. Revisão do Modelo de Negócio
                          </h3>
                          <p className="text-gray-700 mb-3">
                            Avaliar se o Simples ainda é o melhor regime, considerando:
                          </p>
                          <div className="grid md:grid-cols-3 gap-3">
                            <div className="bg-white p-3 rounded-lg">
                              <p className="text-sm font-semibold text-gray-900">Clientes</p>
                              <p className="text-xs text-gray-600">B2B ou B2C?</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                              <p className="text-sm font-semibold text-gray-900">Margens</p>
                              <p className="text-xs text-gray-600">Alta ou baixa?</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                              <p className="text-sm font-semibold text-gray-900">Cadeia de valor</p>
                              <p className="text-xs text-gray-600">Posição estratégica</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <i className="ri-calculator-line text-blue-600"></i>
                            2. Simulações Tributárias Comparativas
                          </h3>
                          <p className="text-gray-700 mb-4">
                            Comparar cenários entre diferentes regimes tributários:
                          </p>
                          <div className="bg-white p-4 rounded-lg mb-3">
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div className="p-2 bg-gray-50 rounded">
                                <p className="text-xs font-semibold text-gray-600">Simples Nacional</p>
                              </div>
                              <div className="p-2 bg-gray-50 rounded">
                                <p className="text-xs font-semibold text-gray-600">Lucro Presumido</p>
                              </div>
                              <div className="p-2 bg-gray-50 rounded">
                                <p className="text-xs font-semibold text-gray-600">Lucro Real</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-yellow-50 p-3 rounded-lg">
                            <p className="text-sm text-gray-900">
                              <strong>⚠️ Atenção:</strong> A decisão sem simulação pode gerar perdas silenciosas significativas.
                            </p>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <i className="ri-database-2-line text-green-600"></i>
                            3. Organização de Dados e Processos
                          </h3>
                          <p className="text-gray-700 mb-3">
                            A Reforma Tributária exige <strong>dados estruturados</strong>, não apenas guias pagas.
                          </p>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                              <span>Cadastro completo e atualizado de produtos/serviços</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                              <span>Classificação fiscal correta (NCM, CEST)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                              <span>Controles de receitas por tipo de operação</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                              <span>Documentação de processos internos</span>
                            </li>
                          </ul>
                        </div>

                        <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <i className="ri-team-line text-orange-600"></i>
                            4. Apoio Técnico Especializado
                          </h3>
                          <p className="text-gray-700 mb-3">
                            A nova legislação traz <strong>zonas cinzentas</strong>, exigindo experiência prática, não apenas leitura da lei.
                          </p>
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm font-semibold text-gray-900 mb-2">Profissionais necessários:</p>
                            <ul className="space-y-1 text-sm text-gray-700">
                              <li>• Contador especializado em planejamento tributário</li>
                              <li>• Consultor com experiência em Reforma Tributária</li>
                              <li>• Advogado tributarista para questões complexas</li>
                              <li>• Analista de sistemas para adequação de ERP</li>
                            </ul>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <i className="ri-git-branch-line text-indigo-600"></i>
                            5. Planejamento de Transição (se necessário)
                          </h3>
                          <p className="text-gray-700 mb-4">
                            Se a decisão for <strong>sair do Simples Nacional</strong>, planeje com antecedência:
                          </p>
                          <div className="space-y-2">
                            <div className="bg-white p-3 rounded-lg flex items-start gap-2">
                              <span className="font-bold text-indigo-600">1.</span>
                              <p className="text-sm text-gray-700">Análise de impacto na folha de pagamento (aumento de encargos)</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg flex items-start gap-2">
                              <span className="font-bold text-indigo-600">2.</span>
                              <p className="text-sm text-gray-700">Revisão de contratos com clientes e fornecedores</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg flex items-start gap-2">
                              <span className="font-bold text-indigo-600">3.</span>
                              <p className="text-sm text-gray-700">Adequação de sistemas e processos fiscais</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg flex items-start gap-2">
                              <span className="font-bold text-indigo-600">4.</span>
                              <p className="text-sm text-gray-700">Capacitação da equipe para novo regime</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg flex items-start gap-2">
                              <span className="font-bold text-indigo-600">5.</span>
                              <p className="text-sm text-gray-700">Comunicação estratégica com stakeholders</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Seção 8 - Papel do Contador */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">O Papel do Contador na Nova Era</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        O contador deixa de ser apenas <strong>operacional</strong> e passa a ser <strong>estratégico</strong>.
                      </p>

                      <div className="bg-gradient-to-br from-teal-50 to-blue-50 border-2 border-teal-300 rounded-xl p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">Empresas do Simples precisarão de profissionais capazes de:</h3>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-line-chart-line"></i>
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Analisar impacto real</h4>
                                <p className="text-sm text-gray-700">Não apenas calcular tributos, mas avaliar impacto no negócio</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-compass-line"></i>
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Orientar mudanças</h4>
                                <p className="text-sm text-gray-700">Assessorar decisões de regime tributário com base em dados</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-shield-check-line"></i>
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Evitar riscos fiscais</h4>
                                <p className="text-sm text-gray-700">Identificar e mitigar passivos antes que se concretizem</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-rocket-line"></i>
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-1">Apoiar crescimento</h4>
                                <p className="text-sm text-gray-700">Planejar expansão considerando impactos tributários</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mt-6">
                          <p className="text-gray-900 font-bold mb-2 flex items-center gap-2">
                            <i className="ri-error-warning-line text-red-600"></i>
                            Na Reforma Tributária, errar custa caro.
                          </p>
                          <p className="text-sm text-gray-700">
                            Empresas que não contam com assessoria contábil estratégica correm riscos graves de <strong>tomar decisões equivocadas</strong> que impactarão resultados por anos.
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* Conclusão */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusão</h2>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        O Simples Nacional <strong>sobrevive</strong>, mas o ambiente ao redor mudou completamente.
                      </p>
                      
                      <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300 rounded-xl p-8 mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Empresas que não se adaptarem podem:</h3>
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <i className="ri-close-circle-fill text-red-600 text-xl mt-0.5"></i>
                            <p className="text-gray-700"><strong>Perder competitividade</strong> em mercados B2B por não gerar crédito tributário</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <i className="ri-close-circle-fill text-red-600 text-xl mt-0.5"></i>
                            <p className="text-gray-700"><strong>Sofrer impactos de caixa</strong> com split payment e novas regras de recolhimento</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <i className="ri-close-circle-fill text-red-600 text-xl mt-0.5"></i>
                            <p className="text-gray-700"><strong>Tomar decisões tributárias equivocadas</strong> por falta de análise técnica adequada</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <i className="ri-close-circle-fill text-red-600 text-xl mt-0.5"></i>
                            <p className="text-gray-700"><strong>Enfrentar problemas operacionais</strong> por sistemas desatualizados e processos inadequados</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Por outro lado, quem se antecipa:</h3>
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-0.5"></i>
                            <p className="text-gray-700"><strong>Transforma a Reforma em vantagem competitiva</strong> através de planejamento estratégico</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-0.5"></i>
                            <p className="text-gray-700"><strong>Toma decisões informadas</strong> baseadas em simulações e análises técnicas</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-0.5"></i>
                            <p className="text-gray-700"><strong>Mantém ou aumenta competitividade</strong> com posicionamento adequado no mercado</p>
                          </div>
                          <div className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-0.5"></i>
                            <p className="text-gray-700"><strong>Opera com segurança e conformidade</strong> evitando riscos fiscais e multas</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl p-8 mt-6">
                        <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                          <i className="ri-lightbulb-line"></i>
                          A nova era não será sobre pagar menos imposto
                        </h3>
                        <p className="text-lg leading-relaxed">
                          Será sobre <strong>pagar certo</strong>, <strong>vender melhor</strong> e <strong>operar com inteligência fiscal</strong>.
                        </p>
                      </div>
                    </section>

                    {/* Sobre o Autor */}
                    <div className="mt-16 p-8 bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-user-star-line text-red-600"></i>
                        Sobre o Autor
                      </h3>
                      <div className="flex items-start gap-6">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                          <i className="ri-user-line text-4xl text-gray-400"></i>
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 mb-2">Dr. Paulo Mendes</h4>
                          <p className="text-gray-700 leading-relaxed mb-3">
                            Especialista em Planejamento Tributário e Reforma Tributária com mais de 20 anos de experiência. Mestre em Direito Tributário pela USP, consultor de empresas de médio e grande porte, palestrante e autor de artigos técnicos sobre estratégias fiscais e compliance.
                          </p>
                          <p className="text-gray-700 text-sm">
                            <strong>Formação:</strong> Mestrado em Direito Tributário (USP), Pós-graduação em Direito Empresarial, Graduação em Contabilidade e Direito<br />
                            <strong>Certificações:</strong> Consultor Tributário CRC, Especialista em Reforma Tributária
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>
                </article>

                {/* Sidebar */}
                <aside className="lg:col-span-4">
                  <div className="sticky top-24 space-y-8">
                    
                    {/* Navegação do Artigo */}
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-list-check text-red-600"></i>
                        Navegação do Artigo
                      </h3>
                      <ul className="space-y-2 text-sm">
                        <li><a href="#introducao" className="text-gray-700 hover:text-red-600 transition-colors">Introdução</a></li>
                        <li><a href="#continua-existindo" className="text-gray-700 hover:text-red-600 transition-colors">O Simples continua existindo?</a></li>
                        <li><a href="#credito-tributario" className="text-gray-700 hover:text-red-600 transition-colors">Principal impacto: crédito tributário</a></li>
                        <li><a href="#novo-dilema" className="text-gray-700 hover:text-red-600 transition-colors">Simples x Regime Regular</a></li>
                        <li><a href="#split-payment" className="text-gray-700 hover:text-red-600 transition-colors">Split Payment</a></li>
                        <li><a href="#simplicidade" className="text-gray-700 hover:text-red-600 transition-colors">Fim da simplicidade operacional</a></li>
                        <li><a href="#cronograma" className="text-gray-700 hover:text-red-600 transition-colors">Cronograma 2026-2033</a></li>
                        <li><a href="#estrategias" className="text-gray-700 hover:text-red-600 transition-colors">Estratégias essenciais</a></li>
                        <li><a href="#papel-contador" className="text-gray-700 hover:text-red-600 transition-colors">Papel do contador</a></li>
                      </ul>
                    </div>

                    {/* Author Info */}
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                          <i className="ri-user-line text-2xl text-gray-400"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">Dr. Paulo Mendes</h4>
                          <p className="text-sm text-gray-600">Especialista em Planejamento Tributário</p>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed mb-4">
                        Mais de 20 anos de experiência em planejamento tributário, com foco em Reforma Tributária e estratégias fiscais para empresas.
                      </p>
                      <button className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors">
                        Ver mais artigos
                      </button>
                    </div>

                    {/* Tags */}
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Simples Nacional</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Reforma Tributária</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">CBS</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">IBS</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Planejamento</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Crédito Tributário</span>
                      </div>
                    </div>
                  </div>
                </aside>

              </div>
            </div>
          </div>

          {/* Feedback Banner */}
          <div className="w-full bg-white">
            <FeedbackBanner 
              articleTitle="Simples Nacional na Nova Era da Reforma Tributária: O Que Muda e Como se Preparar"
              articleUrl={typeof window !== 'undefined' ? window.location.href : ''}
            />
          </div>

        </div>
        
        <Footer />
      </>
    );
  }

  if (slug === 'beneficios-corporativos-pacote-atrativo') {
    return (
      <>
        <SEOHead 
          title="Benefícios Corporativos: Como Estruturar um Pacote Atrativo | Guia Completo 2025"
          description="Guia completo sobre benefícios corporativos: tipos, aspectos legais, contabilização, impactos na Justiça do Trabalho e como estruturar um pacote estratégico para atração e retenção de talentos."
          keywords="benefícios corporativos, pacote de benefícios, RH, recursos humanos, contabilização de benefícios, folha de pagamento, CLT, justiça trabalhista, atração de talentos, retenção de colaboradores"
          canonical={`https://ativonews.com.br/artigo/${slug}`}
        />
        <SchemaOrg type="Article" data={{
          headline: "Benefícios Corporativos: Como Estruturar um Pacote Atrativo",
          description: "Guia completo sobre como estruturar benefícios corporativos de forma estratégica, sustentável e em conformidade legal",
          author: "Dra. Mariana Costa",
          datePublished: "2025-01-15",
          image: "https://readdy.ai/api/search-image?query=corporate%20benefits%20HR%20professional%20business%20meeting%20team%20discussion%20modern%20office%20workspace%20employee%20satisfaction%20workplace%20culture%20professional%20environment%20natural%20lighting&width=1200&height=630&seq=beneficios-corporativos-hero&orientation=landscape"
        }} />
        
        <div className="min-h-screen bg-white">
          <Navbar />
          
          {/* Hero Section */}
          <div className="w-full bg-gradient-to-b from-[#F8FAFC] to-white">
            <div className="max-w-7xl mx-auto px-6 pt-32 pb-16">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-[#64748B] mb-8">
                <Link to="/" className="hover:text-[#1E40AF] transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <Link to="/categoria/recursos-humanos" className="hover:text-[#1E40AF] transition-colors">
                  Recursos Humanos
                </Link>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <span className="text-[#0F172A] font-medium">Benefícios Corporativos</span>
              </nav>

              {/* Category Badge */}
              <div className="mb-6">
                <span className="inline-block px-4 py-2 bg-[#10B981] text-white text-sm font-bold rounded-lg uppercase tracking-wide">
                  Recursos Humanos
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight max-w-4xl">
                Benefícios Corporativos: Como Estruturar um Pacote Atrativo
              </h1>

              {/* Excerpt */}
              <p className="text-xl text-[#475569] mb-8 leading-relaxed max-w-3xl">
                Guia completo para RH, contadores, gestores e empresários sobre como estruturar benefícios corporativos de forma estratégica, sustentável e em conformidade legal.
              </p>

              {/* Author & Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-[#64748B]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-gray-400"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#0F172A]">Dra. Mariana Costa</div>
                    <div className="text-sm">Especialista em RH e Gestão de Pessoas</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  <span>15 de janeiro de 2025</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  <span>20 min</span>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full h-[500px] bg-gray-100">
            <img 
              src="https://readdy.ai/api/search-image?query=diverse%20corporate%20team%20celebrating%20success%20in%20modern%20office%20space%20professional%20workplace%20culture%20employee%20benefits%20wellness%20program%20happy%20professionals%20collaboration%20teamwork%20business%20environment%20natural%20lighting%20high%20quality%20photography&width=1200&height=500&seq=beneficios-corporativos-featured&orientation=landscape"
              alt="Benefícios Corporativos"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content Area */}
          <div className="w-full bg-white py-16">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-12 gap-12">
                
                {/* Main Content */}
                <article className="lg:col-span-8">
                  <div className="prose prose-lg max-w-none">
                    
                    {/* TL;DR */}
                    <div className="bg-green-50 border-l-4 border-green-600 p-6 mb-10 rounded-r-lg">
                      <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-flashlight-line text-green-600"></i>
                        TL;DR — Resumo Executivo
                      </h2>
                      <p className="text-gray-700 leading-relaxed">
                        Benefícios corporativos bem estruturados são ferramentas estratégicas para <strong>atração, retenção e engajamento de talentos</strong>. Vão além das obrigações legais (VT, vale-alimentação quando obrigatório) e incluem planos de saúde, previdência privada, educação, flexibilidade e bem-estar. A chave está em <strong>equilibrar custos, expectativas dos colaboradores e conformidade legal/contábil</strong>. Este guia apresenta tipos de benefícios, aspectos legais críticos (especialmente na Justiça do Trabalho), contabilização correta e um passo a passo para estruturar um pacote sustentável e competitivo.
                      </p>
                    </div>

                    {/* Sumário */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-10">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-list-check text-green-600"></i>
                        Sumário
                      </h2>
                      <ul className="space-y-2 text-gray-700">
                        <li><a href="#o-que-sao" className="hover:text-green-600 transition-colors">→ O que são benefícios corporativos</a></li>
                        <li><a href="#obrigatorios-estrategicos" className="hover:text-green-600 transition-colors">→ Benefícios obrigatórios vs. estratégicos</a></li>
                        <li><a href="#importancia" className="hover:text-green-600 transition-colors">→ Por que benefícios impactam atração e retenção</a></li>
                        <li><a href="#tipos" className="hover:text-green-600 transition-colors">→ Principais tipos de benefícios corporativos</a></li>
                        <li><a href="#passo-a-passo" className="hover:text-green-600 transition-colors">→ Passo a passo para estruturar um pacote de benefícios</a></li>
                        <li><a href="#contabilizacao" className="hover:text-green-600 transition-colors">→ Contabilização e tratamento fiscal dos benefícios</a></li>
                        <li><a href="#justica-trabalhista" className="hover:text-green-600 transition-colors">→ Cuidados na Justiça do Trabalho</a></li>
                        <li><a href="#exemplos" className="hover:text-green-600 transition-colors">→ Exemplos de pacotes de benefícios bem-sucedidos</a></li>
                        <li><a href="#faq" className="hover:text-green-600 transition-colors">→ FAQ</a></li>
                      </ul>
                    </div>

                    {/* Seção 1 */}
                    <section id="o-que-sao" className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">O que são benefícios corporativos</h2>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Benefícios corporativos são <strong>vantagens concedidas pela empresa aos colaboradores além do salário base</strong>. Podem ser obrigatórios (previstos na CLT, acordos coletivos ou convenções) ou estratégicos (oferecidos voluntariamente para atrair e reter talentos).
                      </p>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        O objetivo dos benefícios estratégicos vai além da conformidade legal: eles visam <strong>melhorar a qualidade de vida dos colaboradores, aumentar o engajamento, reduzir turnover e fortalecer a cultura organizacional</strong>.
                      </p>
                    </section>

                    {/* Seção 2 */}
                    <section id="obrigatorios-estrategicos" className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">Benefícios obrigatórios vs. estratégicos</h2>
                      
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-file-list-line text-red-600"></i>
                            Benefícios Obrigatórios
                          </h3>
                          <p className="text-sm text-gray-700 mb-3">
                            Previstos em lei, acordo ou convenção coletiva. Descumprimento gera passivo trabalhista.
                          </p>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-red-600 mt-0.5"></i>
                              <span>13º salário</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-red-600 mt-0.5"></i>
                              <span>Férias remuneradas + 1/3</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-red-600 mt-0.5"></i>
                              <span>FGTS (8%)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-red-600 mt-0.5"></i>
                              <span>Vale-transporte (quando aplicável)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-red-600 mt-0.5"></i>
                              <span>Vale-alimentação/refeição (quando previsto em acordo)</span>
                            </li>
                          </ul>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-5">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-gift-line text-green-600"></i>
                            Benefícios Estratégicos
                          </h3>
                          <p className="text-sm text-gray-700 mb-3">
                            Oferecidos voluntariamente pela empresa para diferenciar-se no mercado.
                          </p>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                              <span>Plano de saúde</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                              <span>Plano odontológico</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                              <span>Previdência privada</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                              <span>Auxílio-educação</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                              <span>Home office e flexibilidade</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </section>

                    {/* Seção 3 */}
                    <section id="importancia" className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">Por que benefícios impactam atração e retenção</h2>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Pesquisas mostram que <strong>até 80% dos profissionais consideram benefícios corporativos tão importantes quanto o salário</strong> na hora de aceitar ou permanecer em um emprego.
                      </p>
                      
                      <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mb-6">
                        <h3 className="font-bold text-gray-900 mb-3">Impactos mensuráveis de um bom pacote de benefícios:</h3>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start gap-2">
                            <i className="ri-arrow-right-circle-fill text-blue-600 mt-0.5"></i>
                            <span><strong>Redução de turnover:</strong> colaboradores satisfeitos permanecem mais tempo na empresa</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-arrow-right-circle-fill text-blue-600 mt-0.5"></i>
                            <span><strong>Aumento de produtividade:</strong> colaboradores com saúde, bem-estar e segurança financeira rendem mais</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-arrow-right-circle-fill text-blue-600 mt-0.5"></i>
                            <span><strong>Atração de talentos qualificados:</strong> bons pacotes atraem profissionais de alto nível</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-arrow-right-circle-fill text-blue-600 mt-0.5"></i>
                            <span><strong>Fortalecimento da marca empregadora:</strong> empresa vista como lugar bom para trabalhar</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-arrow-right-circle-fill text-blue-600 mt-0.5"></i>
                            <span><strong>Redução de absenteísmo:</strong> colaboradores saudáveis faltam menos</span>
                          </li>
                        </ul>
                      </div>
                    </section>

                    {/* Seção 4 */}
                    <section id="tipos" className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">Principais tipos de benefícios corporativos</h2>
                      
                      <div className="space-y-6">
                        {/* Saúde e Bem-estar */}
                        <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-heart-pulse-line text-purple-600"></i>
                            1. Saúde e Bem-estar
                          </h3>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-purple-600 mt-0.5"></i>
                              <span><strong>Plano de saúde:</strong> cobertura médica para colaborador e dependentes</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-purple-600 mt-0.5"></i>
                              <span><strong>Plano odontológico:</strong> cuidados dentários preventivos e corretivos</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-purple-600 mt-0.5"></i>
                              <span><strong>Seguro de vida:</strong> proteção financeira para familiares</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-purple-600 mt-0.5"></i>
                              <span><strong>Programas de bem-estar:</strong> academias, terapia, acompanhamento nutricional</span>
                            </li>
                          </ul>
                        </div>

                        {/* Financeiros */}
                        <div className="bg-white border-2 border-green-200 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-money-dollar-circle-line text-green-600"></i>
                            2. Benefícios Financeiros
                          </h3>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-green-600 mt-0.5"></i>
                              <span><strong>Previdência privada:</strong> contribuição da empresa para aposentadoria do colaborador</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-green-600 mt-0.5"></i>
                              <span><strong>Participação nos lucros (PLR):</strong> bonificação atrelada a resultados</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-green-600 mt-0.5"></i>
                              <span><strong>Stock options:</strong> participação acionária da empresa</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-green-600 mt-0.5"></i>
                              <span><strong>Empréstimos consignados:</strong> condições facilitadas</span>
                            </li>
                          </ul>
                        </div>

                        {/* Desenvolvimento */}
                        <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-graduation-cap-line text-blue-600"></i>
                            3. Desenvolvimento Profissional
                          </h3>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span><strong>Auxílio-educação:</strong> cursos, graduação, pós-graduação, idiomas</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span><strong>Treinamentos internos:</strong> capacitação técnica e comportamental</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span><strong>Plano de carreira estruturado:</strong> crescimento dentro da empresa</span>
                            </li>
                          </ul>
                        </div>

                        {/* Flexibilidade */}
                        <div className="bg-white border-2 border-orange-200 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-time-line text-orange-600"></i>
                            4. Flexibilidade e Qualidade de Vida
                          </h3>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-orange-600 mt-0.5"></i>
                              <span><strong>Home office:</strong> trabalho remoto total ou híbrido</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-orange-600 mt-0.5"></i>
                              <span><strong>Horário flexível:</strong> autonomia para organizar a jornada</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-orange-600 mt-0.5"></i>
                              <span><strong>Licença parental estendida:</strong> além do previsto na CLT</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-orange-600 mt-0.5"></i>
                              <span><strong>Day off no aniversário:</strong> folga no dia do aniversário</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </section>

                    {/* Seção 5 - Passo a Passo */}
                    <section id="passo-a-passo" className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Passo a passo para estruturar um pacote de benefícios</h2>
                      
                      <div className="space-y-4">
                        <div className="flex items-start gap-4 bg-gradient-to-r from-teal-50 to-transparent border-l-4 border-teal-600 p-4 rounded-r-lg">
                          <div className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            1
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Diagnóstico: entenda a realidade da empresa</h3>
                            <p className="text-sm text-gray-700">
                              • Capacidade financeira para investir em benefícios<br />
                              • Perfil demográfico dos colaboradores (idade, estado civil, filhos)<br />
                              • Principais necessidades e expectativas do time<br />
                              • Práticas de mercado no setor
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-4 bg-gradient-to-r from-blue-50 to-transparent border-l-4 border-blue-600 p-4 rounded-r-lg">
                          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            2
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Pesquisa de expectativas: ouça os colaboradores</h3>
                            <p className="text-sm text-gray-700">
                              Faça pesquisas internas para entender quais benefícios são mais valorizados. Nem sempre o que a empresa acha importante é o que os colaboradores desejam.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-4 bg-gradient-to-r from-purple-50 to-transparent border-l-4 border-purple-600 p-4 rounded-r-lg">
                          <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            3
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Benchmarking: analise o mercado</h3>
                            <p className="text-sm text-gray-700">
                              Pesquise o que empresas concorrentes e do mesmo setor oferecem. Use pesquisas salariais e relatórios de mercado.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-4 bg-gradient-to-r from-green-50 to-transparent border-l-4 border-green-600 p-4 rounded-r-lg">
                          <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            4
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Defina orçamento e prioridades</h3>
                            <p className="text-sm text-gray-700">
                              Estabeleça quanto a empresa pode investir em benefícios (geralmente entre 15% e 30% da folha de pagamento) e priorize os benefícios mais estratégicos.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-4 bg-gradient-to-r from-orange-50 to-transparent border-l-4 border-orange-600 p-4 rounded-r-lg">
                          <div className="w-10 h-10 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            5
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Escolha fornecedores e negocie</h3>
                            <p className="text-sm text-gray-700">
                              Pesquise operadoras de plano de saúde, seguradoras, instituições de ensino e outros fornecedores. Negocie condições e valores.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-4 bg-gradient-to-r from-red-50 to-transparent border-l-4 border-red-600 p-4 rounded-r-lg">
                          <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            6
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Documente em política interna</h3>
                            <p className="text-sm text-gray-700">
                              Crie um documento formal descrevendo todos os benefícios, critérios de elegibilidade, regras de uso e procedimentos. Isso evita conflitos e garante transparência.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-4 bg-gradient-to-r from-indigo-50 to-transparent border-l-4 border-indigo-600 p-4 rounded-r-lg">
                          <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            7
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Comunique de forma clara</h3>
                            <p className="text-sm text-gray-700">
                              Divulgue o pacote de benefícios de forma atrativa: apresentações, manuais, intranet. Colaboradores precisam conhecer e valorizar o que recebem.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-4 bg-gradient-to-r from-pink-50 to-transparent border-l-4 border-pink-600 p-4 rounded-r-lg">
                          <div className="w-10 h-10 rounded-full bg-pink-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                            8
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-1">Monitore e revise periodicamente</h3>
                            <p className="text-sm text-gray-700">
                              Acompanhe a utilização dos benefícios, satisfação dos colaboradores e custo-benefício. Ajuste o pacote conforme necessário.
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Seção 6 - Contabilização */}
                    <section id="contabilizacao" className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Contabilização e tratamento fiscal dos benefícios</h2>
                      
                      <p className="text-gray-700 leading-relaxed mb-6">
                        A <strong>contabilização correta dos benefícios</strong> é fundamental para conformidade fiscal, gestão de custos e transparência. Diferentes benefícios têm tratamentos contábeis e fiscais específicos.
                      </p>

                      <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 rounded-r-lg mb-6">
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-alert-line text-yellow-600"></i>
                          Princípio Geral:
                        </h3>
                        <p className="text-gray-700">
                          Benefícios concedidos a colaboradores devem ser registrados como <strong>despesas operacionais</strong> (despesas com pessoal) no resultado da empresa. Alguns benefícios têm natureza indenizatória e não integram a base de cálculo de encargos trabalhistas e tributários.
                        </p>
                      </div>

                      <div className="space-y-6">
                        {/* Plano de Saúde */}
                        <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">Plano de Saúde e Odontológico</h3>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Contabilização:</h4>
                              <p className="text-sm text-gray-700">
                                <strong>Débito:</strong> Despesas com Benefícios (Plano de Saúde)<br />
                                <strong>Crédito:</strong> Contas a Pagar (Operadora de Saúde)
                              </p>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Tratamento Fiscal:</h4>
                              <ul className="text-sm text-gray-700 space-y-1">
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                                  <span><strong>Não integra base de cálculo de INSS, FGTS e IRRF</strong> quando pago diretamente à operadora</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                                  <span><strong>Dedutível no IRPJ/CSLL</strong> (Lucro Real) como despesa operacional</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                                  <span>Se reembolsado ao colaborador, pode integrar a base de cálculo dos encargos</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Vale-Alimentação/Refeição */}
                        <div className="bg-white border-2 border-green-200 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">Vale-Alimentação e Vale-Refeição</h3>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Contabilização:</h4>
                              <p className="text-sm text-gray-700">
                                <strong>Débito:</strong> Despesas com Benefícios (Vale-Alimentação)<br />
                                <strong>Crédito:</strong> Contas a Pagar (Fornecedor de Cartões)
                              </p>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Tratamento Fiscal:</h4>
                              <ul className="text-sm text-gray-700 space-y-1">
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                                  <span><strong>Não integra base de INSS, FGTS e IRRF</strong> se fornecido dentro do Programa de Alimentação do Trabalhador (PAT)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-green-600 mt-0.5"></i>
                                  <span>Empresa inscrita no PAT pode ter incentivo fiscal (dedução adicional no IRPJ)</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Previdência Privada */}
                        <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">Previdência Privada</h3>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Contabilização:</h4>
                              <p className="text-sm text-gray-700">
                                <strong>Débito:</strong> Despesas com Benefícios (Previdência Privada)<br />
                                <strong>Crédito:</strong> Contas a Pagar (Instituição Financeira)
                              </p>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Tratamento Fiscal:</h4>
                              <ul className="text-sm text-gray-700 space-y-1">
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-purple-600 mt-0.5"></i>
                                  <span><strong>Não integra base de INSS e FGTS</strong> quando pago diretamente à instituição</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-purple-600 mt-0.5"></i>
                                  <span><strong>Pode integrar base de IRRF</strong> dependendo da modalidade do plano</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-purple-600 mt-0.5"></i>
                                  <span>Dedutível no IRPJ/CSLL como despesa operacional</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Auxílio-Educação */}
                        <div className="bg-white border-2 border-orange-200 rounded-lg p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">Auxílio-Educação</h3>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Contabilização:</h4>
                              <p className="text-sm text-gray-700">
                                <strong>Débito:</strong> Despesas com Treinamento e Desenvolvimento<br />
                                <strong>Crédito:</strong> Contas a Pagar (Instituição de Ensino) ou Contas a Pagar (Colaborador, se reembolso)
                              </p>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Tratamento Fiscal:</h4>
                              <ul className="text-sm text-gray-700 space-y-1">
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-orange-600 mt-0.5"></i>
                                  <span><strong>Não integra base de encargos</strong> se pago diretamente à instituição de ensino e relacionado à atividade da empresa</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-arrow-right-s-line text-orange-600 mt-0.5"></i>
                                  <span>Se reembolsado ao colaborador sem comprovação, pode integrar a folha</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mt-6">
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-error-warning-line text-red-600"></i>
                          Atenção Crítica:
                        </h3>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          <li className="flex items-start gap-2">
                            <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                            <span>Benefícios pagos em <strong>dinheiro diretamente ao colaborador</strong> tendem a integrar a base de cálculo de encargos</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                            <span>Mantenha <strong>documentação completa</strong>: notas fiscais, recibos, comprovantes de pagamento</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                            <span>Consulte sempre um <strong>contador especializado</strong> para garantir conformidade</span>
                          </li>
                        </ul>
                      </div>
                    </section>

                    {/* Seção 7 - Justiça Trabalhista */}
                    <section id="justica-trabalhista" className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Cuidados na Justiça do Trabalho</h2>
                      
                      <p className="text-gray-700 leading-relaxed mb-6">
                        Benefícios mal estruturados ou concedidos de forma irregular podem gerar <strong>passivos trabalhistas significativos</strong>. A Justiça do Trabalho tem jurisprudência consolidada sobre diversos temas envolvendo benefícios corporativos.
                      </p>

                      <div className="space-y-6">
                        {/* Risco 1 */}
                        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-alert-line text-red-600"></i>
                            1. Integração à Remuneração (Súmula 367 do TST)
                          </h3>
                          <p className="text-gray-700 mb-3">
                            Benefícios fornecidos de forma <strong>habitual e em dinheiro</strong> podem ser considerados salário in natura e integrar a base de cálculo de 13º, férias, FGTS e verbas rescisórias.
                          </p>
                          <p className="text-gray-700 text-sm">
                            <strong>Como evitar:</strong> Forneça benefícios in natura (cartão, voucher, pagamento direto ao fornecedor), não em dinheiro. Documente que o benefício tem caráter indenizatório, não salarial.
                          </p>
                        </div>

                        {/* Risco 2 */}
                        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-alert-line text-red-600"></i>
                            2. Supressão de Benefícios (Princípio da Inalterabilidade Contratual Lesiva)
                          </h3>
                          <p className="text-gray-700 mb-3">
                            Retirar benefícios já concedidos (plano de saúde, auxílio-educação) sem justificativa pode ser interpretado como <strong>alteração lesiva do contrato de trabalho</strong> e gerar indenização.
                          </p>
                          <p className="text-gray-700 text-sm">
                            <strong>Como evitar:</strong> Deixe claro na política de benefícios que são concessões temporárias, sujeitas a revisão. Se precisar suprimir, faça de forma gradual, com comunicação transparente e, se possível, ofereça alternativas.
                          </p>
                        </div>

                        {/* Risco 3 */}
                        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-alert-line text-red-600"></i>
                            3. Isonomia e Discriminação
                          </h3>
                          <p className="text-gray-700 mb-3">
                            Oferecer benefícios de forma <strong>discriminatória</strong> (por gênero, idade, raça) ou sem critérios objetivos pode configurar prática ilícita e gerar ações por danos morais.
                          </p>
                          <p className="text-gray-700 text-sm">
                            <strong>Como evitar:</strong> Estabeleça critérios claros e objetivos (cargo, tempo de casa, desempenho). Documente as regras e aplique de forma uniforme.
                          </p>
                        </div>

                        {/* Risco 4 */}
                        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-alert-line text-red-600"></i>
                            4. Falta de Documentação
                          </h3>
                          <p className="text-gray-700 mb-3">
                            Não documentar formalmente os benefícios oferecidos pode dificultar a defesa em processos trabalhistas. O colaborador pode alegar que recebeu mais do que efetivamente teve.
                          </p>
                          <p className="text-gray-700 text-sm">
                            <strong>Como evitar:</strong> Crie uma política formal de benefícios, faça os colaboradores assinarem termo de adesão e mantenha controles de utilização (recibos, comprovantes).
                          </p>
                        </div>

                        {/* Risco 5 */}
                        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-alert-line text-red-600"></i>
                            5. Desconto Indevido em Folha
                          </h3>
                          <p className="text-gray-700 mb-3">
                            Descontar valores de benefícios (plano de saúde, vale-transporte) sem <strong>autorização expressa e por escrito</strong> do colaborador é ilegal.
                          </p>
                          <p className="text-gray-700 text-sm">
                            <strong>Como evitar:</strong> Sempre que houver coparticipação do colaborador em algum benefício, obtenha autorização formal por escrito antes do desconto.
                          </p>
                        </div>
                      </div>

                      <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mt-6">
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-shield-check-line text-blue-600"></i>
                          Boas Práticas para Reduzir Riscos Trabalhistas:
                        </h3>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start gap-2">
                            <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                            <span>Documente tudo: política de benefícios, termos de adesão, comunicações</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                            <span>Deixe claro na política que benefícios não têm natureza salarial</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                            <span>Forneça benefícios in natura, não em dinheiro</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                            <span>Estabeleça critérios objetivos e transparentes</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                            <span>Revise periodicamente a política com apoio jurídico</span>
                          </li>
                        </ul>
                      </div>
                    </section>

                    {/* Seção 8 - Exemplos */}
                    <section id="exemplos" className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Exemplos de pacotes de benefícios bem-sucedidos</h2>
                      
                      <div className="space-y-6">
                        {/* Exemplo 1 - Startup */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-rocket-line text-purple-600"></i>
                            Startup de Tecnologia (50 colaboradores)
                          </h3>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Pacote de Benefícios:</h4>
                              <ul className="space-y-1 text-gray-700 text-sm">
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                                  <span>Plano de saúde (coparticipação de 20%)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                                  <span>Vale-refeição/alimentação (R$ 30/dia útil)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                                  <span>Home office 100% (economiza vale-transporte)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                                  <span>Auxílio home office (R$ 200/mês para internet e energia)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                                  <span>Plataforma de cursos online ilimitada</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                                  <span>Day off no aniversário</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-purple-600 mt-0.5"></i>
                                  <span>Stock options (após 1 ano)</span>
                                </li>
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Resultados:</h4>
                              <p className="text-sm text-gray-700">
                                • Turnover reduzido de 35% para 12% ao ano<br />
                                • Aumento de 40% em candidaturas qualificadas<br />
                                • Satisfação dos colaboradores: 4,6/5
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Exemplo 2 - Indústria */}
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-building-line text-blue-600"></i>
                            Indústria de Médio Porte (300 colaboradores)
                          </h3>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Pacote de Benefícios:</h4>
                              <ul className="space-y-1 text-gray-700 text-sm">
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                                  <span>Plano de saúde (empresa paga 100% para colaborador, 50% para dependentes)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                                  <span>Plano odontológico</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                                  <span>Seguro de vida</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                                  <span>Vale-alimentação (R$ 500/mês)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                                  <span>Vale-transporte</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                                  <span>Participação nos Lucros e Resultados (PLR)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                                  <span>Convênio com farmácia (descontos)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-blue-600 mt-0.5"></i>
                                  <span>Programa de segurança e saúde ocupacional robusto</span>
                                </li>
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Resultados:</h4>
                              <p className="text-sm text-gray-700">
                                • Redução de 45% em afastamentos por doenças<br />
                                • Aumento de produtividade de 18%<br />
                                • Zero reclamações trabalhistas sobre benefícios em 3 anos
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Exemplo 3 - Varejo */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-store-2-line text-green-600"></i>
                            Rede de Varejo (1.000+ colaboradores)
                          </h3>
                          <div className="space-y-3">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Pacote de Benefícios:</h4>
                              <ul className="space-y-1 text-gray-700 text-sm">
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                                  <span>Plano de saúde (operadora com rede ampla)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                                  <span>Vale-alimentação (cartão flexível)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                                  <span>Vale-transporte</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                                  <span>Cesta de Natal</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                                  <span>Desconto em produtos da loja (20%)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                                  <span>Programa de reconhecimento (destaque do mês)</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                                  <span>Treinamentos internos constantes</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <i className="ri-check-line text-green-600 mt-0.5"></i>
                                  <span>Plano de carreira estruturado</span>
                                </li>
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-1">Resultados:</h4>
                              <p className="text-sm text-gray-700">
                                • Turnover reduzido de 60% para 38% (setor tem alta rotatividade)<br />
                                • Melhoria no atendimento ao cliente (NPS +15 pontos)<br />
                                • Economia de R$ 2 milhões/ano em custos de recrutamento e treinamento
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* FAQ */}
                    <section id="faq" className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Perguntas Frequentes (FAQ)</h2>
                      
                      <div className="space-y-4">
                        <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                            <i className="ri-question-line text-green-600 mt-0.5"></i>
                            <span>Qual o percentual ideal da folha para investir em benefícios?</span>
                          </h3>
                          <p className="text-gray-700 text-sm ml-7">
                            Em geral, empresas investem entre <strong>15% e 30% da folha de pagamento</strong> em benefícios. O ideal varia conforme setor, porte da empresa e perfil dos colaboradores. Startups podem focar em flexibilidade e desenvolvimento; indústrias em saúde e segurança.
                          </p>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                            <i className="ri-question-line text-green-600 mt-0.5"></i>
                            <span>Benefícios em dinheiro integram a folha de pagamento?</span>
                          </h3>
                          <p className="text-gray-700 text-sm ml-7">
                            Sim, <strong>benefícios pagos em dinheiro diretamente ao colaborador</strong> tendem a integrar a base de cálculo de INSS, FGTS, IRRF e outras verbas trabalhistas. Por isso, o ideal é fornecer benefícios in natura (cartão, voucher, pagamento direto ao fornecedor).
                          </p>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                            <i className="ri-question-line text-green-600 mt-0.5"></i>
                            <span>Posso retirar benefícios já concedidos?</span>
                          </h3>
                          <p className="text-gray-700 text-sm ml-7">
                            Depende. Se o benefício foi <strong>incorporado ao contrato</strong> ou concedido habitualmente por longo período, sua supressão pode ser considerada alteração lesiva e gerar passivo trabalhista. Sempre documente que benefícios são concessões temporárias e sujeitas a revisão.
                          </p>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                            <i className="ri-question-line text-green-600 mt-0.5"></i>
                            <span>Quais benefícios são mais valorizados pelos colaboradores?</span>
                          </h3>
                          <p className="text-gray-700 text-sm ml-7">
                            Segundo pesquisas recentes: <strong>1) Plano de saúde, 2) Flexibilidade (home office/horário flexível), 3) Vale-alimentação, 4) Auxílio-educação, 5) Previdência privada</strong>. Mas isso varia conforme perfil demográfico (idade, estado civil, região).
                          </p>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                            <i className="ri-question-line text-green-600 mt-0.5"></i>
                            <span>Como medir o retorno sobre investimento (ROI) em benefícios?</span>
                          </h3>
                          <p className="text-gray-700 text-sm ml-7">
                            Acompanhe indicadores como: <strong>turnover, absenteísmo, satisfação dos colaboradores (pesquisas internas), produtividade, tempo médio de permanência na empresa e custo de recrutamento</strong>. Compare antes e depois da implementação dos benefícios.
                          </p>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                            <i className="ri-question-line text-green-600 mt-0.5"></i>
                            <span>Pequenas empresas podem oferecer bons benefícios?</span>
                          </h3>
                          <p className="text-gray-700 text-sm ml-7">
                            Sim! Pequenas empresas podem focar em <strong>benefícios de baixo custo e alto impacto</strong>: flexibilidade de horário, day off no aniversário, plataformas de cursos online (custo baixo por colaborador), reconhecimento e cultura organizacional forte. Não é apenas sobre dinheiro.
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* Conclusão */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">Conclusão</h2>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Estruturar um pacote de benefícios corporativos atrativo não é apenas uma questão de <strong>competitividade no mercado de trabalho</strong> — é uma decisão estratégica que impacta diretamente produtividade, satisfação, retenção de talentos e resultados financeiros da empresa.
                      </p>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        O segredo está em <strong>equilibrar custos, expectativas dos colaboradores e conformidade legal</strong>. Benefícios bem estruturados, documentados e comunicados geram valor para ambos os lados: empresa economiza com turnover e absenteísmo; colaboradores ganham qualidade de vida e segurança.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        Este guia apresentou conceitos, tipos de benefícios, passo a passo para estruturação, aspectos contábeis e cuidados na Justiça do Trabalho. Use essas informações para construir um pacote de benefícios que fortaleça sua empresa e valorize quem faz a diferença no dia a dia: seus colaboradores.
                      </p>
                    </section>

                    {/* Sobre o Autor */}
                    <div className="mt-16 p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-user-star-line text-green-600"></i>
                        Sobre a Autora
                      </h3>
                      <div className="flex items-start gap-6">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                          <i className="ri-user-line text-4xl text-gray-400"></i>
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 mb-2">Dra. Mariana Costa</h4>
                          <p className="text-gray-700 leading-relaxed mb-3">
                            Especialista em Recursos Humanos e Gestão de Pessoas com mais de 15 anos de experiência. Mestre em Administração pela FGV, com foco em gestão estratégica de pessoas e benefícios corporativos. Consultora de RH para empresas de médio e grande porte, palestrante e autora de artigos sobre atração, retenção de talentos e cultura organizacional.
                          </p>
                          <p className="text-gray-700 text-sm">
                            <strong>Formação:</strong> Mestrado em Administração (FGV), MBA em Gestão de Pessoas, Graduação em Psicologia<br />
                            <strong>Certificações:</strong> SHRM-CP (Society for Human Resource Management), Coaching Executivo
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>
                </article>

                {/* Sidebar */}
                <aside className="lg:col-span-4">
                  <div className="sticky top-24 space-y-8">
                    
                    {/* Author Info */}
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                          <i className="ri-user-line text-2xl text-gray-400"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">Dra. Mariana Costa</h4>
                          <p className="text-sm text-gray-600">Especialista em RH e Gestão de Pessoas</p>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Mais de 15 anos de experiência em gestão estratégica de pessoas, benefícios corporativos e cultura organizacional.
                      </p>
                    </div>

                    {/* Newsletter */}
                    <div className="bg-gradient-to-br from-[#10B981] to-[#059669] rounded-lg p-6 text-white">
                      <h3 className="text-lg font-bold mb-3">Receba nossas notícias</h3>
                      <p className="text-sm mb-4">
                        Assine nossa newsletter para receber conteúdos sobre RH, gestão de pessoas e tendências do mercado.
                      </p>
                      <div className="space-y-3">
                        <input 
                          type="email" 
                          placeholder="Seu e-mail"
                          className="w-full px-4 py-3 bg-white/20 rounded-lg border border-white/30 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white/50"
                        />
                        <button className="w-full bg-white text-[#10B981] py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                          Assinar
                        </button>
                      </div>
                    </div>
                  </div>
                </aside>

              </div>
            </div>
          </div>

          {/* Feedback Banner */}
          <div className="w-full bg-white">
            <FeedbackBanner 
              articleTitle="Benefícios Corporativos: Como Estruturar um Pacote Atrativo"
              articleUrl={typeof window !== 'undefined' ? window.location.href : ''}
            />
          </div>

        </div>
        
        <Footer />
      </>
    );
  }

  // Novo conteúdo para Recrutamento e Seleção
  if (slug === 'recrutamento-selecao-melhores-praticas-dp') {
    return (
      <>
        <SEOHead 
          title="Recrutamento e Seleção: Melhores Práticas para o Departamento Pessoal | Guia Completo 2025"
          description="Guia técnico completo sobre recrutamento e seleção para o DP: planejamento, triagem, entrevistas estruturadas, documentação admissional, onboarding, indicadores e conformidade legal trabalhista."
          keywords="recrutamento e seleção, departamento pessoal, DP, RH, contratação, admissão, entrevista estruturada, onboarding, eSocial, compliance trabalhista, CLT, documentação admissional, indicadores de RH"
          canonical={`https://ativonews.com.br/artigo/${slug}`}
        />
        <SchemaOrg type="Article" data={{
          headline: "Recrutamento e Seleção: Melhores Práticas para o Departamento Pessoal",
          description: "Guia técnico sobre melhores práticas de recrutamento e seleção para o DP, com foco em eficiência, conformidade legal e redução de riscos trabalhistas",
          author: "Dra. Mariana Costa",
          datePublished: "2024-11-20",
          image: "https://readdy.ai/api/search-image?query=professional%20HR%20recruitment%20process%20job%20interview%20modern%20office%20business%20meeting%20candidate%20selection%20structured%20interview%20corporate%20environment%20professional%20lighting%20clean%20design&width=1200&height=630&seq=recrutamento-selecao-dp-hero&orientation=landscape"
        }} />
        
        <div className="min-h-screen bg-white">
          <Navbar />
          
          {/* Hero Section */}
          <div className="w-full bg-gradient-to-b from-[#F8FAFC] to-white">
            <div className="max-w-7xl mx-auto px-6 pt-32 pb-16">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-[#64748B] mb-8">
                <Link to="/" className="hover:text-[#1E40AF] transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <Link to="/categoria/departamento-pessoal" className="hover:text-[#1E40AF] transition-colors">
                  Departamento Pessoal
                </Link>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
                <span className="text-[#0F172A] font-medium">Recrutamento e Seleção</span>
              </nav>

              {/* Category Badge */}
              <div className="mb-6">
                <span className="inline-block px-4 py-2 bg-[#F59E0B] text-white text-sm font-bold rounded-lg uppercase tracking-wide">
                  Departamento Pessoal
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight max-w-4xl">
                Recrutamento e Seleção: Melhores Práticas para o Departamento Pessoal
              </h1>

              {/* Excerpt */}
              <p className="text-xl text-[#475569] mb-8 leading-relaxed max-w-3xl">
                Guia técnico completo sobre as melhores práticas de Recrutamento e Seleção para o DP, com foco em eficiência, conformidade legal e redução de riscos trabalhistas.
              </p>

              {/* Author & Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-[#64748B]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-gray-400"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#0F172A]">Dra. Mariana Costa</div>
                    <div className="text-sm">Especialista em RH e Gestão de Pessoas</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  <span>20 de novembro de 2024</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  <span>12 min</span>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full h-[500px] bg-gray-100">
            <img 
              src="https://readdy.ai/api/search-image?query=professional%20HR%20recruitment%20interview%20modern%20office%20business%20meeting%20diverse%20candidates%20structured%20selection%20process%20corporate%20environment%20professional%20team%20workplace%20professional%20lighting%20clean%20contemporary%20design&width=1200&height=500&seq=recrutamento-selecao-featured&orientation=landscape"
              alt="Recrutamento e Seleção para o DP"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content Area */}
          <div className="w-full bg-white py-16">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-12 gap-12">
                
                {/* Main Content */}
                <article className="lg:col-span-8">
                  <div className="prose prose-lg max-w-none">
                    
                    {/* TL;DR */}
                    <div className="bg-orange-50 border-l-4 border-orange-600 p-6 mb-10 rounded-r-lg">
                      <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <i className="ri-flashlight-line text-orange-600"></i>
                        TL;DR — Resumo Executivo
                      </h2>
                      <p className="text-gray-700 leading-relaxed">
                        O Recrutamento e Seleção (R&S) é um dos processos mais estratégicos do Departamento Pessoal. <strong>Uma contratação mal conduzida gera custos trabalhistas, rotatividade, riscos legais e perda de produtividade.</strong> Este guia apresenta as melhores práticas de R&S para o DP, abordando: planejamento da vaga, divulgação estratégica, triagem estruturada, entrevistas padronizadas, avaliação criteriosa, documentação admissional completa, onboarding eficiente, indicadores de performance e principais erros a evitar. O DP não atua apenas na admissão — seu papel é garantir conformidade legal, documentação correta e dados confiáveis ao eSocial, evitando passivos futuros.
                      </p>
                    </div>

                    {/* Introdução */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">Introdução</h2>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        O <strong>Recrutamento e Seleção (R&S)</strong> é um dos processos mais estratégicos do Departamento Pessoal (DP). Uma contratação mal conduzida gera custos trabalhistas, rotatividade, riscos legais e perda de produtividade. Por isso, aplicar boas práticas estruturadas deixou de ser opção e passou a ser <strong>necessidade</strong>.
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        Este artigo apresenta as melhores práticas de Recrutamento e Seleção para o DP, com foco em <strong>eficiência, conformidade legal e redução de riscos</strong>.
                      </p>
                    </section>

                    {/* Seção 1 - Papel do DP */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">O Papel do DP no Recrutamento e Seleção</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        O DP não atua apenas na admissão. Seu papel é garantir que o processo:
                      </p>
                      <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-shield-check-line text-blue-600"></i>
                            Conformidade Legal
                          </h3>
                          <p className="text-sm text-gray-700">Atenda à legislação trabalhista (CLT, normas regulamentadoras)</p>
                        </div>
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-5">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-file-list-3-line text-green-600"></i>
                            Documentação Correta
                          </h3>
                          <p className="text-sm text-gray-700">Seja documentalmente correto e rastreável</p>
                        </div>
                        <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-5">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-alert-line text-purple-600"></i>
                            Prevenção de Passivos
                          </h3>
                          <p className="text-sm text-gray-700">Evite passivos trabalhistas futuros</p>
                        </div>
                        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-5">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-database-2-line text-orange-600"></i>
                            Dados Confiáveis
                          </h3>
                          <p className="text-sm text-gray-700">Assegure dados confiáveis ao eSocial</p>
                        </div>
                      </div>
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                        <p className="text-gray-700">
                          <strong>⚠️ Alerta Crítico:</strong> R&S sem integração com o DP é <strong>risco jurídico</strong>.
                        </p>
                      </div>
                    </section>

                    {/* Seção 2 - Planejamento da Vaga */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Planejamento da Vaga</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        Antes de divulgar a vaga, o DP deve validar:
                      </p>
                      <div className="space-y-4">
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <div className="flex items-start gap-3">
                            <i className="ri-file-text-line text-blue-600 text-xl mt-0.5"></i>
                            <div>
                              <h3 className="font-bold text-gray-900 mb-1">Descrição Clara do Cargo</h3>
                              <p className="text-sm text-gray-700">Título, atribuições, responsabilidades e requisitos objetivos</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <div className="flex items-start gap-3">
                            <i className="ri-time-line text-green-600 text-xl mt-0.5"></i>
                            <div>
                              <h3 className="font-bold text-gray-900 mb-1">Jornada de Trabalho</h3>
                              <p className="text-sm text-gray-700">Carga horária semanal, horários, regime de trabalho (presencial/híbrido/remoto)</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <div className="flex items-start gap-3">
                            <i className="ri-file-list-line text-purple-600 text-xl mt-0.5"></i>
                            <div>
                              <h3 className="font-bold text-gray-900 mb-1">Tipo de Contrato</h3>
                              <p className="text-sm text-gray-700">CLT, temporário, estágio, aprendiz, PJ (atenção aos riscos)</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <div className="flex items-start gap-3">
                            <i className="ri-money-dollar-circle-line text-orange-600 text-xl mt-0.5"></i>
                            <div>
                              <h3 className="font-bold text-gray-900 mb-1">Faixa Salarial e Benefícios</h3>
                              <p className="text-sm text-gray-700">Salário base, benefícios (VT, VR/VA, plano de saúde, etc.)</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                          <div className="flex items-start gap-3">
                            <i className="ri-team-line text-teal-600 text-xl mt-0.5"></i>
                            <div>
                              <h3 className="font-bold text-gray-900 mb-1">Enquadramento Sindical</h3>
                              <p className="text-sm text-gray-700">Sindicato aplicável, convenção coletiva, categorias profissionais</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg mt-6">
                        <p className="text-gray-700">
                          <strong>💡 Dica Importante:</strong> Um erro nessa etapa impacta folha, encargos e compliance.
                        </p>
                      </div>
                    </section>

                    {/* Seção 3 - Divulgação Estratégica */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Divulgação Estratégica</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        Boas práticas de divulgação incluem:
                      </p>
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-8">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-chat-check-line text-blue-600 text-2xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-2">Linguagem Objetiva</h3>
                                <p className="text-sm text-gray-700">Clara, não discriminatória e alinhada à legislação (evitar termos como "sexo", "idade", "boa aparência")</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-information-line text-green-600 text-2xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-2">Informações Essenciais</h3>
                                <p className="text-sm text-gray-700">Cargo, requisitos mínimos, localização, regime de trabalho</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-checkbox-circle-line text-purple-600 text-2xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-2">Alinhamento com Realidade</h3>
                                <p className="text-sm text-gray-700">Descrição compatível com o que será exercido no dia a dia</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-global-line text-orange-600 text-2xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-2">Canais Adequados</h3>
                                <p className="text-sm text-gray-700">LinkedIn, sites de emprego, divulgação interna, universidades</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mt-6">
                        <p className="text-gray-700">
                          <strong>⚠️ Atenção:</strong> Evite promessas que não possam ser cumpridas contratualmente (exemplo: "salário acima da média" sem definição clara).
                        </p>
                      </div>
                    </section>

                    {/* Seção 4 - Triagem de Currículos */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Triagem de Currículos</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        A triagem deve considerar:
                      </p>
                      <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 bg-blue-50 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-checkbox-line text-blue-600"></i>
                            Requisitos Técnicos Mínimos
                          </h3>
                          <p className="text-sm text-gray-700">Formação, certificações, conhecimentos específicos exigidos para a função</p>
                        </div>
                        <div className="border-l-4 border-green-500 bg-green-50 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-briefcase-line text-green-600"></i>
                            Experiência Compatível
                          </h3>
                          <p className="text-sm text-gray-700">Tempo de experiência e atuação em funções similares</p>
                        </div>
                        <div className="border-l-4 border-purple-500 bg-purple-50 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-line-chart-line text-purple-600"></i>
                            Estabilidade Profissional
                          </h3>
                          <p className="text-sm text-gray-700">Histórico de permanência em empresas anteriores (alta rotatividade pode indicar problemas)</p>
                        </div>
                        <div className="border-l-4 border-orange-500 bg-orange-50 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-alert-line text-orange-600"></i>
                            Riscos Trabalhistas Ocultos
                          </h3>
                          <p className="text-sm text-gray-700">Processos trabalhistas em andamento, incompatibilidade de informações</p>
                        </div>
                      </div>
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg mt-6">
                        <p className="text-gray-700">
                          <strong>📋 Importante:</strong> Sempre mantenha <strong>registro do processo</strong>, mesmo para candidatos não aprovados (evidência de critérios objetivos em caso de questionamento).
                        </p>
                      </div>
                    </section>

                    {/* Seção 5 - Entrevistas Estruturadas */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Entrevistas Estruturadas</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        Entrevistas devem seguir roteiro padronizado para garantir critérios objetivos e reduzir vieses:
                      </p>
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-8 mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">Componentes de uma Entrevista Estruturada:</h3>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-question-line"></i>
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-2">Perguntas Técnicas e Comportamentais</h4>
                                <p className="text-sm text-gray-700">Roteiro pré-definido com perguntas sobre conhecimentos técnicos e competências comportamentais (STAR method: Situação, Tarefa, Ação, Resultado)</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-star-line"></i>
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-2">Avaliação Objetiva</h4>
                                <p className="text-sm text-gray-700">Sistema de pontuação para cada critério avaliado (escala de 1 a 5, por exemplo)</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-file-list-line"></i>
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-2">Registro das Respostas</h4>
                                <p className="text-sm text-gray-700">Documentação das principais respostas e observações para posterior comparação entre candidatos</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-orange-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-focus-line"></i>
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-2">Critérios Claros de Decisão</h4>
                                <p className="text-sm text-gray-700">Definição prévia dos pesos de cada critério (ex: experiência 30%, técnico 40%, comportamental 30%)</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                        <p className="text-gray-700 mb-3">
                          <strong>🚫 Perguntas Proibidas por Lei:</strong>
                        </p>
                        <ul className="space-y-2 text-sm text-gray-700 ml-4">
                          <li className="flex items-start gap-2">
                            <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                            <span>Sobre vida pessoal (estado civil, filhos, intenção de engravidar)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                            <span>Religião ou orientação política</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                            <span>Condições de saúde não relacionadas à função</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-close-circle-fill text-red-600 mt-0.5"></i>
                            <span>Orientação sexual ou identidade de gênero</span>
                          </li>
                        </ul>
                      </div>
                    </section>

                    {/* Seção 6 - Avaliação e Decisão */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Avaliação e Decisão</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        A decisão final deve ser baseada em critérios objetivos e documentados:
                      </p>
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-checkbox-circle-line text-blue-600"></i>
                            Aderência ao Cargo
                          </h3>
                          <p className="text-sm text-gray-700">O candidato possui as competências técnicas e comportamentais necessárias?</p>
                        </div>
                        <div className="bg-white border-2 border-green-200 rounded-lg p-6">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-shield-check-line text-green-600"></i>
                            Risco Jurídico
                          </h3>
                          <p className="text-sm text-gray-700">Há processos trabalhistas pendentes? Informações inconsistentes?</p>
                        </div>
                        <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-money-dollar-circle-line text-purple-600"></i>
                            Compatibilidade Salarial
                          </h3>
                          <p className="text-sm text-gray-700">As expectativas salariais estão alinhadas com o orçamento da vaga?</p>
                        </div>
                        <div className="bg-white border-2 border-orange-200 rounded-lg p-6">
                          <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-time-line text-orange-600"></i>
                            Histórico Profissional
                          </h3>
                          <p className="text-sm text-gray-700">Tempo de permanência em empregos anteriores, evolução de carreira</p>
                        </div>
                      </div>
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                        <p className="text-gray-700">
                          <strong>⚠️ Lembre-se:</strong> Contratar rápido não pode significar <strong>contratar errado</strong>. O custo de uma contratação inadequada é muito maior que o tempo investido em um processo bem estruturado.
                        </p>
                      </div>
                    </section>

                    {/* Seção 7 - Documentação Admissional */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Documentação Admissional</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        Antes do início das atividades, o DP deve garantir:
                      </p>
                      <div className="space-y-4">
                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-600 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-file-list-3-line text-blue-600"></i>
                            Documentos Obrigatórios Completos
                          </h3>
                          <ul className="text-sm text-gray-700 space-y-2 mt-3 ml-4">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span>RG, CPF, título de eleitor, carteira de trabalho (CTPS digital)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span>Comprovante de residência atualizado</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span>Certificado de escolaridade</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span>Foto 3x4</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span>Carteira de Vacinação (quando aplicável)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span>Certidão de Nascimento ou Casamento</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-line text-blue-600 mt-0.5"></i>
                              <span>Dados bancários para depósito</span>
                            </li>
                          </ul>
                        </div>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-heart-pulse-line text-green-600"></i>
                            Exames Admissionais Realizados
                          </h3>
                          <p className="text-sm text-gray-700 mt-2">Exame médico admissional obrigatório (NR-7 - PCMSO), com ASO assinado pelo médico do trabalho antes do início das atividades</p>
                        </div>
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-600 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-file-text-line text-purple-600"></i>
                            Contrato Assinado
                          </h3>
                          <p className="text-sm text-gray-700 mt-2">Contrato de trabalho detalhado, com cargo, salário, jornada, local de trabalho e cláusulas obrigatórias</p>
                        </div>
                        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-600 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <i className="ri-database-2-line text-orange-600"></i>
                            Cadastro Correto no eSocial
                          </h3>
                          <p className="text-sm text-gray-700 mt-2">Evento S-2200 (Admissão) enviado <strong>antes do início das atividades</strong> (obrigatório por lei)</p>
                        </div>
                      </div>
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mt-6">
                        <p className="text-gray-700">
                          <strong>🚨 Atenção Crítica:</strong> <strong>Admissão irregular gera multas e passivos.</strong> O DP é responsável por garantir que todos os requisitos legais sejam cumpridos antes do primeiro dia de trabalho.
                        </p>
                      </div>
                    </section>

                    {/* Seção 8 - Integração e Onboarding */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Integração e Onboarding</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        O onboarding vai além da admissão formal. É o processo de integração do novo colaborador à cultura, processos e expectativas da empresa.
                      </p>
                      <div className="bg-gradient-to-br from-teal-50 to-blue-50 border-2 border-teal-200 rounded-xl p-8 mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">Boas Práticas de Onboarding:</h3>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-file-list-3-line text-teal-600 text-2xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-2">Integração Formal Documentada</h4>
                                <p className="text-sm text-gray-700">Checklist de integração com assinatura do novo colaborador e responsável pelo onboarding</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-book-open-line text-blue-600 text-2xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-2">Normas Internas</h4>
                                <p className="text-sm text-gray-700">Apresentação do regulamento interno, políticas de compliance, código de ética</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-shield-check-line text-green-600 text-2xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-2">Políticas e Códigos de Conduta</h4>
                                <p className="text-sm text-gray-700">Segurança da informação, uso de equipamentos, LGPD, assédio moral e sexual</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white p-5 rounded-lg">
                            <div className="flex items-start gap-3">
                              <i className="ri-edit-line text-purple-600 text-2xl mt-0.5"></i>
                              <div>
                                <h4 className="font-bold text-gray-900 mb-2">Registro da Ciência</h4>
                                <p className="text-sm text-gray-700">Assinatura de termos comprovando que o colaborador tomou ciência das políticas (evidência em processos trabalhistas)</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                        <p className="text-gray-700">
                          <strong>✅ Benefício Comprovado:</strong> Onboarding estruturado reduz <strong>turnover nos primeiros 90 dias em até 50%</strong> e <strong>diminui riscos legais</strong> relacionados a desconhecimento de normas internas.
                        </p>
                      </div>
                    </section>

                    {/* Seção 9 - Indicadores */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Indicadores de Recrutamento e Seleção</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        O DP deve acompanhar indicadores (KPIs) para avaliar a eficiência do processo de R&S:
                      </p>
                      <div className="space-y-4">
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-time-line text-xl"></i>
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Tempo Médio de Contratação (Time to Hire)</h3>
                              <p className="text-gray-700">Dias entre abertura da vaga e admissão. Meta: 30-45 dias para cargos operacionais, 60-90 dias para cargos estratégicos.</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-refresh-line text-xl"></i>
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Taxa de Rotatividade (Turnover)</h3>
                              <p className="text-gray-700">Percentual de desligamentos nos primeiros 90, 180 e 365 dias. Alta rotatividade inicial indica falhas no R&S.</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-money-dollar-circle-line text-xl"></i>
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Custo por Contratação</h3>
                              <p className="text-gray-700">Soma de custos com anúncios, ferramentas, tempo de equipe, exames. Importante para avaliar ROI do processo.</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-orange-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-alert-line text-xl"></i>
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-900 mb-2">Reincidência de Desligamentos</h3>
                              <p className="text-gray-700">Número de vezes que a mesma vaga foi aberta. Reincidência alta indica problema estrutural (salário, cultura, gestão).</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded-r-lg mt-6">
                        <p className="text-gray-700">
                          <strong>📊 Dica de Gestão:</strong> Dados orientam decisões melhores. Estabeleça metas para cada indicador e revise periodicamente para identificar oportunidades de melhoria.
                        </p>
                      </div>
                    </section>

                    {/* Seção 10 - Principais Erros */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Principais Erros a Evitar</h2>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        Conheça os erros mais comuns no processo de R&S e como evitá-los:
                      </p>
                      <div className="space-y-4">
                        <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2">❌ Erro 1: Falta de Registro do Processo</h3>
                          <p className="text-sm text-gray-700 mb-2">Sem documentação, não há como comprovar critérios objetivos em caso de questionamento judicial.</p>
                          <p className="text-sm font-semibold text-gray-900">✅ Solução: Documente todas as etapas (divulgação, triagem, entrevistas, avaliação, decisão).</p>
                        </div>
                        <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2">❌ Erro 2: Contratações sem Validação do DP</h3>
                          <p className="text-sm text-gray-700 mb-2">Gestores contratam sem consultar o DP, gerando riscos legais e inconsistências no eSocial.</p>
                          <p className="text-sm font-semibold text-gray-900">✅ Solução: O DP deve validar todas as contratações antes da admissão.</p>
                        </div>
                        <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2">❌ Erro 3: Informações Divergentes da Realidade</h3>
                          <p className="text-sm text-gray-700 mb-2">Prometer salário, benefícios ou condições que não serão cumpridas gera passivo trabalhista.</p>
                          <p className="text-sm font-semibold text-gray-900">✅ Solução: Alinhe expectativas desde a divulgação da vaga.</p>
                        </div>
                        <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2">❌ Erro 4: Ausência de Critérios Objetivos</h3>
                          <p className="text-sm text-gray-700 mb-2">Decisões baseadas em "feeling" ou preferências pessoais aumentam risco de discriminação.</p>
                          <p className="text-sm font-semibold text-gray-900">✅ Solução: Use entrevistas estruturadas e avaliação objetiva com critérios pré-definidos.</p>
                        </div>
                        <div className="border-l-4 border-red-500 bg-red-50 p-5 rounded-r-lg">
                          <h3 className="font-bold text-gray-900 mb-2">❌ Erro 5: Admissão sem Documentação Completa</h3>
                          <p className="text-sm text-gray-700 mb-2">Colaborador começa a trabalhar antes de exames, contrato assinado ou envio ao eSocial.</p>
                          <p className="text-sm font-semibold text-gray-900">✅ Solução: Checklist obrigatório antes do primeiro dia de trabalho.</p>
                        </div>
                      </div>
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg mt-6">
                        <p className="text-gray-700">
                          <strong>⚖️ Risco Legal:</strong> R&S sem método gera problemas trabalhistas. Processos bem estruturados protegem a empresa e o DP em fiscalizações e reclamações.
                        </p>
                      </div>
                    </section>

                    {/* Conclusão */}
                    <section className="mb-10">
                      <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusão</h2>
                      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-8">
                        <p className="text-gray-700 leading-relaxed mb-4">
                          O Recrutamento e Seleção é um processo estratégico que impacta diretamente a <strong>produtividade, clima organizacional e riscos trabalhistas</strong> da empresa.
                        </p>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          O papel do DP vai muito além da admissão: ele é <strong>guardião da conformidade legal, da documentação correta e da qualidade das contratações</strong>.
                        </p>
                        <div className="bg-white p-6 rounded-lg mt-6">
                          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <i className="ri-lightbulb-line text-orange-600 text-xl"></i>
                            Principais Conclusões:
                          </h3>
                          <ul className="space-y-3">
                            <li className="flex items-start gap-2 text-gray-700">
                              <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                              <span><strong>Estruture o processo:</strong> Planejamento, divulgação, triagem, entrevistas, avaliação e decisão devem seguir critérios objetivos</span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                              <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                              <span><strong>Documente tudo:</strong> Registros protegem a empresa em questionamentos judiciais</span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                              <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                              <span><strong>Conformidade legal é prioridade:</strong> Documentação admissional completa e envio ao eSocial antes do início das atividades</span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                              <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                              <span><strong>Onboarding reduz turnover:</strong> Integração estruturada aumenta retenção e reduz riscos legais</span>
                            </li>
                            <li className="flex items-start gap-2 text-gray-700">
                              <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                              <span><strong>Indicadores orientam melhorias:</strong> Acompanhe tempo de contratação, turnover, custo e reincidência</span>
                            </li>
                          </ul>
                        </div>
                        <p className="text-gray-700 leading-relaxed mt-6 font-semibold">
                          Empresas que investem em processos de R&S bem estruturados reduzem custos, evitam passivos trabalhistas e constroem equipes mais engajadas e produtivas.
                        </p>
                      </div>
                    </section>

                    {/* Sobre o Autor */}
                    <div className="mt-16 p-8 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-user-star-line text-orange-600"></i>
                        Sobre a Autora
                      </h3>
                      <div className="flex items-start gap-6">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                          <i className="ri-user-line text-4xl text-gray-400"></i>
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900 mb-2">Dra. Mariana Costa</h4>
                          <p className="text-gray-700 leading-relaxed mb-3">
                            Especialista em Recursos Humanos e Gestão de Pessoas com mais de 15 anos de experiência. Mestre em Administração pela FGV, com foco em gestão estratégica de pessoas, recrutamento e seleção, e compliance trabalhista. Consultora de RH para empresas de médio e grande porte, palestrante e autora de artigos sobre atração, retenção de talentos e boas práticas em Departamento Pessoal.
                          </p>
                          <p className="text-gray-700 text-sm">
                            <strong>Formação:</strong> Mestrado em Administração (FGV), MBA em Gestão de Pessoas, Graduação em Psicologia<br />
                            <strong>Certificações:</strong> SHRM-CP (Society for Human Resource Management), Coaching Executivo, Especialista em eSocial e Legislação Trabalhista
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>
                </article>

                {/* Sidebar */}
                <aside className="lg:col-span-4">
                  <div className="sticky top-24 space-y-8">
                    
                    {/* Navegação do Artigo */}
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-list-check text-orange-600"></i>
                        Navegação do Artigo
                      </h3>
                      <ul className="space-y-2 text-sm">
                        <li><a href="#introducao" className="text-gray-700 hover:text-orange-600 transition-colors">→ Introdução</a></li>
                        <li><a href="#papel-dp" className="text-gray-700 hover:text-orange-600 transition-colors">→ Papel do DP no R&S</a></li>
                        <li><a href="#planejamento" className="text-gray-700 hover:text-orange-600 transition-colors">→ Planejamento da Vaga</a></li>
                        <li><a href="#divulgacao" className="text-gray-700 hover:text-orange-600 transition-colors">→ Divulgação Estratégica</a></li>
                        <li><a href="#triagem" className="text-gray-700 hover:text-orange-600 transition-colors">→ Triagem de Currículos</a></li>
                        <li><a href="#entrevistas" className="text-gray-700 hover:text-orange-600 transition-colors">→ Entrevistas Estruturadas</a></li>
                        <li><a href="#avaliacao" className="text-gray-700 hover:text-orange-600 transition-colors">→ Avaliação e Decisão</a></li>
                        <li><a href="#documentacao" className="text-gray-700 hover:text-orange-600 transition-colors">→ Documentação Admissional</a></li>
                        <li><a href="#onboarding" className="text-gray-700 hover:text-orange-600 transition-colors">→ Integração e Onboarding</a></li>
                        <li><a href="#indicadores" className="text-gray-700 hover:text-orange-600 transition-colors">→ Indicadores de R&S</a></li>
                        <li><a href="#erros" className="text-gray-700 hover:text-orange-600 transition-colors">→ Principais Erros</a></li>
                      </ul>
                    </div>

                    {/* Author Info */}
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                          <i className="ri-user-line text-2xl text-gray-400"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">Dra. Mariana Costa</h4>
                          <p className="text-sm text-gray-600">Especialista em RH e Gestão de Pessoas</p>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed mb-4">
                        Mais de 15 anos de experiência em gestão estratégica de pessoas, recrutamento, seleção e compliance trabalhista.
                      </p>
                      <button className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors">
                        Ver mais artigos
                      </button>
                    </div>

                    {/* Tags */}
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Recrutamento</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Seleção</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Departamento Pessoal</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">RH</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">Onboarding</span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">eSocial</span>
                      </div>
                    </div>
                  </div>
                </aside>

              </div>
            </div>
          </div>

          {/* Feedback Banner */}
          <div className="w-full bg-white">
            <FeedbackBanner 
              articleTitle="Recrutamento e Seleção: Melhores Práticas para o Departamento Pessoal"
              articleUrl={typeof window !== 'undefined' ? window.location.href : ''}
            />
          </div>

        </div>
        
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead 
        title={`${article.title} - Ativo News`}
        description={article.excerpt}
        keywords={article.category}
        canonical={`https://ativonews.com.br/artigo/${slug}`}
      />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        author: article.author,
        datePublished: article.date,
        image: article.image
      }} />
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section - Full Width */}
        <div className="w-full bg-gradient-to-b from-[#F8FAFC] to-white">
          <div className="max-w-7xl mx-auto px-6 pt-32 pb-16">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-[#64748B] mb-8">
              <Link to="/" className="hover:text-[#1E40AF] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <Link to={`/categoria/${article.category.toLowerCase().replace(/\s+/g, '-')}`} className="hover:text-[#1E40AF] transition-colors">
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <span className="text-[#0F172A] font-medium">Artigo</span>
            </nav>

            {/* Category Badge */}
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-[#1E40AF] text-white text-sm font-bold rounded-lg uppercase tracking-wide">
                {article.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight max-w-4xl">
              {article.title}
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-[#475569] mb-8 leading-relaxed max-w-3xl">
              {article.excerpt}
            </p>

            {/* Author & Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-[#64748B]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                  {article.author.avatar ? (
                    <img src={article.author.avatar} alt={article.author.name} className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-user-line text-2xl text-gray-400"></i>
                  )}
                </div>
                <div>
                  <div className="font-semibold text-[#0F172A]">{article.author.name}</div>
                  <div className="text-sm">{article.author.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                <span>{new Date(article.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Image - Full Width */}
        <div className="w-full h-[500px] bg-gray-100">
          <img 
            src={article.image} 
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Content Area - Two Column Layout */}
        <div className="w-full bg-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-12 gap-12">
              
              {/* Main Content - 8 columns */}
              <article className="lg:col-span-8">
                <div className="prose prose-lg max-w-none">
                  {article.slug === 'cbs-ibs-nova-emissao-nota-fiscal-reforma-tributaria' ? (
                    // Conteúdo específico do artigo CBS e IBS
                    <>
                      {/* TL;DR - Resumo Executivo */}
                      <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-10 rounded-r-lg">
                        <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-flashlight-line text-teal-600"></i>
                          TL;DR — Resumo Executivo
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                          A partir de <strong>1º de janeiro de 2026</strong>, todos os documentos fiscais eletrônicos (NF-e, NFC-e, CT-e, NFS-e) deverão destacar CBS e IBS conforme a Reforma Tributária. Não é uma "nota nova", mas sim <strong>adequação de layout</strong> dos documentos atuais. A NFS-e padrão nacional torna-se obrigatória, unificando emissão em todos os municípios. Empresas precisam atualizar ERPs, revisar cadastros de produtos/serviços, treinar equipes e acompanhar notas técnicas da Receita Federal. O cronograma prevê testes em 2026, cobrança efetiva de CBS em 2027, transição do IBS a partir de 2029 e extinção completa de ICMS/ISS em 2033. Quem não se preparar enfrentará rejeições de documentos, multas e problemas operacionais graves.
                        </p>
                      </div>

                      {/* Seção 1 */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">O que são CBS e IBS na prática?</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          A <strong>CBS (Contribuição sobre Bens e Serviços)</strong> e o <strong>IBS (Imposto sobre Bens e Serviços)</strong> são os dois novos tributos criados pela Reforma Tributária brasileira, regulamentada pela <a href="https://www.planalto.gov.br/ccivil_03/leis/lcp/lcp214.htm" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Lei Complementar 214/2025</a>.
                        </p>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          A CBS substitui PIS e COFINS (tributos federais), enquanto o IBS substitui ICMS e ISS (tributos estaduais e municipais). Ambos funcionam como <strong>IVA (Imposto sobre Valor Agregado)</strong>, com não-cumulatividade plena e incidência sobre o destino (onde o bem/serviço é consumido).
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                          Para empresas, isso significa que <strong>a forma de calcular, destacar e recolher tributos sobre vendas muda completamente</strong>. E essa mudança começa pelos documentos fiscais eletrônicos que você emite todos os dias.
                        </p>
                      </section>

                      {/* Seção 2 */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Quais documentos fiscais mudam em 2026?</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Segundo as <a href="https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/acoes-e-programas/programas-e-atividades/reforma-consumo/orientacoes-2026" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Orientações 2026 da Receita Federal</a>, os seguintes documentos fiscais eletrônicos (DF-e) serão adequados para destacar CBS, IBS e Imposto Seletivo (IS):
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                          <li><strong>NF-e</strong> (Nota Fiscal Eletrônica) — modelo 55</li>
                          <li><strong>NFC-e</strong> (Nota Fiscal de Consumidor Eletrônica) — modelo 65</li>
                          <li><strong>CT-e</strong> (Conhecimento de Transporte Eletrônico)</li>
                          <li><strong>CT-e OS</strong> (Conhecimento de Transporte Eletrônico para Outros Serviços)</li>
                          <li><strong>NFS-e</strong> (Nota Fiscal de Serviços Eletrônica) — padrão nacional</li>
                        </ul>
                        <p className="text-gray-700 leading-relaxed">
                          <strong>Importante:</strong> não se trata de criar um documento novo do zero. Os DF-e existentes continuam, mas com <strong>novos campos, grupos de dados e regras de validação</strong> para comportar os tributos da Reforma Tributária.
                        </p>
                      </section>

                      {/* Seção 3 */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">NF-e e NFC-e: o que muda no layout e por que afeta o ERP</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          A <a href="https://www.nfe.fazenda.gov.br/portal/exibirArquivo.aspx?conteudo=AklZnck3o6I=" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Nota Técnica 2025.002 (RTC)</a> detalha as adequações de layout da NF-e e NFC-e. As principais mudanças incluem:
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                          <li><strong>Novos grupos de tributação:</strong> campos específicos para CBS, IBS e Imposto Seletivo (alíquota, base de cálculo, valor do imposto, créditos)</li>
                          <li><strong>Identificação de regime tributário:</strong> indicadores de contribuinte CBS/IBS, regime de apuração, tipo de operação (tributada, isenta, imune, não-incidência)</li>
                          <li><strong>Destaque de créditos:</strong> informação de créditos de CBS/IBS a serem apropriados pelo adquirente</li>
                          <li><strong>Classificação fiscal:</strong> NCM/CEST com regras específicas para CBS/IBS/IS</li>
                          <li><strong>Validações automáticas:</strong> o sistema SEFAZ rejeitará notas com inconsistências nos novos campos</li>
                        </ul>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          <strong>Impacto no ERP:</strong> seu sistema emissor de notas precisa ser atualizado para:
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                          <li>Gerar XML com os novos grupos de dados</li>
                          <li>Calcular CBS/IBS/IS corretamente com base em tabelas de alíquotas e exceções</li>
                          <li>Validar dados antes do envio (evitar rejeições)</li>
                          <li>Imprimir DANFE com destaque dos novos tributos</li>
                          <li>Integrar com cadastros atualizados de produtos/serviços</li>
                        </ul>
                        <p className="text-gray-700 leading-relaxed">
                          <strong>Atenção:</strong> ERPs desatualizados não conseguirão emitir NF-e/NFC-e a partir de 2026. Consulte seu fornecedor de software sobre cronograma de atualização e testes.
                        </p>
                      </section>

                      {/* Seção 4 */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">NFS-e nacional: por que é um marco em 2026</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          A partir de <strong>janeiro de 2026</strong>, a <a href="https://www.gov.br/fazenda/pt-br/assuntos/noticias/2025/agosto/a-partir-de-janeiro-de-2026-a-nota-fiscal-de-servico-eletronica-nfs-e-sera-obrigatoria-a-fim-de-simplificar-cotidiano-das-empresas" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">NFS-e padrão nacional torna-se obrigatória</a> em todos os municípios brasileiros.
                        </p>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Até 2025, cada município tinha seu próprio sistema de NFS-e, com layouts, regras e integrações diferentes. Isso gerava custos altíssimos para empresas que prestam serviços em múltiplos municípios (necessidade de integrar com centenas de sistemas diferentes).
                        </p>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          <strong>O que muda:</strong>
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                          <li><strong>Padrão único nacional:</strong> um só layout XML para todos os municípios</li>
                          <li><strong>Integração simplificada:</strong> uma única integração técnica para emitir NFS-e em qualquer cidade</li>
                          <li><strong>Destaque de CBS e IBS:</strong> a NFS-e nacional já nasce preparada para a Reforma Tributária</li>
                          <li><strong>Redução de custos:</strong> menos complexidade técnica, menos manutenção de integrações</li>
                        </ul>
                        <p className="text-gray-700 leading-relaxed">
                          <strong>Ação necessária:</strong> empresas prestadoras de serviços devem atualizar seus sistemas para emitir NFS-e no padrão nacional e garantir que os campos de CBS/IBS estejam corretamente preenchidos.
                        </p>
                      </section>

                      {/* Seção 5 - Cronograma */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Cronograma 2026–2033: o que muda em cada ano</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          A transição para CBS e IBS não acontece de uma vez. Segundo o <a href="https://www.camara.leg.br/noticias/1237089-reforma-tributaria-comeca-fase-de-transicao-com-testes-de-novos-impostos-em-2026/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">cronograma oficial</a>, a implementação é gradual:
                        </p>
                        
                        <div className="overflow-x-auto mb-6">
                          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead className="bg-teal-600 text-white">
                              <tr>
                                <th className="px-6 py-3 text-left text-sm font-semibold">Ano</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold">O que acontece</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold">Impacto operacional</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              <tr className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-semibold text-gray-900">2026</td>
                                <td className="px-6 py-4 text-sm text-gray-700">Fase de testes e adaptação. Documentos fiscais devem destacar CBS/IBS, mas sem cobrança efetiva. NFS-e nacional obrigatória.</td>
                                <td className="px-6 py-4 text-sm text-gray-700">Atualização de ERPs, treinamento de equipes, testes de emissão, revisão de cadastros.</td>
                              </tr>
                              <tr className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-semibold text-gray-900">2027</td>
                                <td className="px-6 py-4 text-sm text-gray-700">Início da cobrança efetiva de CBS (federal) e Imposto Seletivo. PIS/COFINS começam a ser reduzidos.</td>
                                <td className="px-6 py-4 text-sm text-gray-700">Cálculo real de CBS, recolhimento, apuração de créditos, ajustes de preços.</td>
                              </tr>
                              <tr className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-semibold text-gray-900">2029</td>
                                <td className="px-6 py-4 text-sm text-gray-700">Início da transição do IBS (estadual/municipal). ICMS e ISS começam a ser reduzidos gradualmente.</td>
                                <td className="px-6 py-4 text-sm text-gray-700">Coexistência de tributos antigos e novos, complexidade de apuração aumenta temporariamente.</td>
                              </tr>
                              <tr className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-semibold text-gray-900">2033</td>
                                <td className="px-6 py-4 text-sm text-gray-700">Conclusão da transição. ICMS e ISS extintos. Apenas CBS, IBS e Imposto Seletivo em vigor.</td>
                                <td className="px-6 py-4 text-sm text-gray-700">Sistema tributário simplificado, mas exige conformidade total com novas regras.</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <p className="text-gray-700 leading-relaxed">
                          <strong>Conclusão:</strong> 2026 é o ano crítico para preparação. Quem não se adaptar enfrentará problemas operacionais graves a partir de 2027.
                        </p>
                      </section>

                      {/* Seção 6 - Checklist */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Checklist prático de preparação (Fiscal + TI + Processos)</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Use este checklist para garantir que sua empresa estará pronta em 2026:
                        </p>

                        <div className="bg-gray-50 p-6 rounded-lg mb-4">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-file-list-3-line text-teal-600"></i>
                            Área Fiscal
                          </h3>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Revisar cadastro de produtos/serviços: NCM, CEST, classificação fiscal correta</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Identificar quais operações são tributadas, isentas, imunes ou não-incidentes para CBS/IBS</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Mapear regimes especiais, benefícios fiscais e exceções aplicáveis</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Acompanhar publicação de notas técnicas e orientações da Receita Federal</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Treinar equipe fiscal sobre cálculo de CBS/IBS e apropriação de créditos</span>
                            </li>
                          </ul>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-lg mb-4">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-code-box-line text-teal-600"></i>
                            Área de TI
                          </h3>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Contatar fornecedor do ERP/emissor de notas: confirmar cronograma de atualização</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Testar emissão de NF-e/NFC-e/NFS-e em ambiente de homologação (SEFAZ)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Validar integração com cadastros de produtos, clientes e fornecedores</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Garantir que o sistema calcula CBS/IBS/IS corretamente (motor tributário atualizado)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Preparar rotinas de backup e contingência para emissão de documentos fiscais</span>
                            </li>
                          </ul>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <i className="ri-team-line text-teal-600"></i>
                            Processos e Governança
                          </h3>
                          <ul className="space-y-2 text-gray-700">
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Criar comitê interno de Reforma Tributária (Fiscal + TI + Financeiro + Jurídico)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Documentar processos de emissão de documentos fiscais (fluxogramas, responsáveis)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Estabelecer rotina de monitoramento de rejeições e erros de emissão</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Revisar contratos com clientes/fornecedores: cláusulas sobre repasse de tributos</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <i className="ri-checkbox-blank-circle-fill text-teal-600 text-xs mt-1.5"></i>
                              <span>Planejar comunicação com clientes sobre mudanças em documentos fiscais</span>
                            </li>
                          </ul>
                        </div>
                      </section>

                      {/* Seção 7 - Erros comuns */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Erros comuns que geram rejeição e risco de inconformidade</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Com base em experiências de outras mudanças de layout (como a versão 4.0 da NF-e), os erros mais comuns que empresas cometem são:
                        </p>

                        <div className="space-y-4">
                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">❌ Cadastro de produtos desatualizado ou incompleto</h3>
                            <p className="text-gray-700 text-sm">NCM incorreto, CEST ausente, classificação fiscal errada. Resultado: cálculo de CBS/IBS incorreto e rejeição da nota.</p>
                          </div>

                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">❌ ERP não atualizado ou atualização parcial</h3>
                            <p className="text-gray-700 text-sm">Sistema não gera XML com novos campos obrigatórios. Nota é rejeitada pelo SEFAZ. Operação para até atualização.</p>
                          </div>

                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">❌ Falta de testes em ambiente de homologação</h3>
                            <p className="text-gray-700 text-sm">Empresa só descobre problemas quando tenta emitir nota em produção. Gera atrasos, multas e problemas com clientes.</p>
                          </div>

                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">❌ Equipe não treinada</h3>
                            <p className="text-gray-700 text-sm">Colaboradores não sabem preencher novos campos, não entendem regras de CBS/IBS. Erros manuais aumentam.</p>
                          </div>

                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">❌ Não acompanhar atualizações de notas técnicas</h3>
                            <p className="text-gray-700 text-sm">Receita Federal publica versões atualizadas de notas técnicas. Quem não acompanha fica com regras desatualizadas.</p>
                          </div>

                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">❌ Ignorar validações do schema XML</h3>
                            <p className="text-gray-700 text-sm">XML mal formado, campos obrigatórios ausentes, tipos de dados incorretos. Rejeição automática.</p>
                          </div>
                        </div>

                        <p className="text-gray-700 leading-relaxed mt-6">
                          <strong>Recomendação:</strong> faça um diagnóstico completo da sua operação fiscal e tecnológica <strong>ainda em 2025</strong>. Quanto antes identificar problemas, mais tempo terá para corrigi-los.
                        </p>
                      </section>

                      {/* Seção 8 - FAQ */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Perguntas Frequentes (FAQ)</h2>
                        
                        <div className="space-y-4">
                          <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                              <i className="ri-question-line text-teal-600 mt-0.5"></i>
                              <span>A NF-e atual deixa de existir em 2026?</span>
                            </h3>
                            <p className="text-gray-700 text-sm ml-7">
                              Não. A NF-e continua existindo. O que muda é o <strong>layout (estrutura do XML)</strong> para incluir campos de CBS, IBS e Imposto Seletivo. É uma adequação, não uma substituição.
                            </p>
                          </div>

                          <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                              <i className="ri-question-line text-teal-600 mt-0.5"></i>
                              <span>Meu ERP precisa ser atualizado obrigatoriamente?</span>
                            </h3>
                            <p className="text-gray-700 text-sm ml-7">
                              Sim. Sem atualização, seu sistema não conseguirá gerar XML no novo layout e as notas serão rejeitadas pelo SEFAZ. Consulte seu fornecedor de software sobre cronograma de atualização.
                            </p>
                          </div>

                          <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                              <i className="ri-question-line text-teal-600 mt-0.5"></i>
                              <span>Em 2026 já vou pagar CBS e IBS?</span>
                            </h3>
                            <p className="text-gray-700 text-sm ml-7">
                              Não. 2026 é ano de testes. Os tributos devem ser destacados nos documentos fiscais, mas <strong>sem cobrança efetiva</strong>. A cobrança de CBS começa em 2027 e do IBS em 2029.
                            </p>
                          </div>

                          <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                              <i className="ri-question-line text-teal-600 mt-0.5"></i>
                              <span>O que é a NFS-e padrão nacional?</span>
                            </h3>
                            <p className="text-gray-700 text-sm ml-7">
                              É um layout único de Nota Fiscal de Serviços Eletrônica válido para todos os municípios brasileiros. Antes, cada cidade tinha seu próprio sistema. A partir de 2026, há um padrão nacional obrigatório.
                            </p>
                          </div>

                          <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                              <i className="ri-question-line text-teal-600 mt-0.5"></i>
                              <span>Onde encontro as notas técnicas oficiais?</span>
                            </h3>
                            <p className="text-gray-700 text-sm ml-7">
                              No portal da NF-e (<a href="https://www.nfe.fazenda.gov.br" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">www.nfe.fazenda.gov.br</a>) e nas <a href="https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/acoes-e-programas/programas-e-atividades/reforma-consumo/orientacoes-2026" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Orientações 2026 da Receita Federal</a>. Acompanhe atualizações regularmente.
                            </p>
                          </div>

                          <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                              <i className="ri-question-line text-teal-600 mt-0.5"></i>
                              <span>O que acontece se eu não me adaptar até 2026?</span>
                            </h3>
                            <p className="text-gray-700 text-sm ml-7">
                              Suas notas fiscais serão rejeitadas pelo SEFAZ. Você não conseguirá emitir documentos fiscais, o que paralisa vendas, faturamento e operações. Além disso, há risco de multas por inconformidade.
                            </p>
                          </div>
                        </div>
                      </section>

                      {/* Seção 9 - Conclusão */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Conclusão: preparação é a chave</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          A Reforma Tributária não é um evento pontual, mas um <strong>processo de transformação que se estende até 2033</strong>. A emissão de documentos fiscais eletrônicos com destaque de CBS e IBS é apenas a ponta do iceberg.
                        </p>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Empresas que se prepararem com antecedência — atualizando sistemas, treinando equipes, revisando cadastros e acompanhando notas técnicas — terão vantagem competitiva. Quem deixar para última hora enfrentará rejeições, multas, atrasos operacionais e perda de competitividade.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                          <strong>Ação recomendada:</strong> monte um comitê interno de Reforma Tributária, faça um diagnóstico completo da sua operação fiscal e tecnológica, e estabeleça um cronograma de adequação. O tempo para se preparar é agora.
                        </p>
                      </section>

                      {/* Referências */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Referências e fontes oficiais</h2>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start gap-2">
                            <i className="ri-link text-teal-600 mt-1"></i>
                            <a href="https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/acoes-e-programas/programas-e-atividades/reforma-consumo/orientacoes-2026" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Orientações 2026 — Receita Federal do Brasil</a>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-link text-teal-600 mt-1"></i>
                            <a href="https://www.nfe.fazenda.gov.br/portal/exibirArquivo.aspx?conteudo=AklZnck3o6I=" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Nota Técnica 2025.002 (RTC) — NF-e e NFC-e</a>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-link text-teal-600 mt-1"></i>
                            <a href="https://www.planalto.gov.br/ccivil_03/leis/lcp/lcp214.htm" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Lei Complementar 214/2025 — Planalto</a>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-link text-teal-600 mt-1"></i>
                            <a href="https://www.gov.br/fazenda/pt-br/assuntos/noticias/2025/agosto/a-partir-de-janeiro-de-2026-a-nota-fiscal-de-servico-eletronica-nfs-e-sera-obrigatoria-a-fim-de-simplificar-cotidiano-das-empresas" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">NFS-e nacional obrigatória a partir de 2026 — Gov.br</a>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-link text-teal-600 mt-1"></i>
                            <a href="https://www.camara.leg.br/noticias/1237089-reforma-tributaria-comeca-fase-de-transicao-com-testes-de-novos-impostos-em-2026/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Cronograma da Reforma Tributária — Câmara dos Deputados</a>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-link text-teal-600 mt-1"></i>
                            <a href="https://blog.tecnospeed.com.br/nota-fiscal-eletronica-nf-e-tudo-que-voce-precisa-saber/" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Nota Fiscal Eletrônica: tudo que você precisa saber — TecnoSpeed</a>
                          </li>
                        </ul>
                      </section>
                    </>
                  ) : article.slug === 'contabilidade-digital-futuro' ? (
                    // Novo conteúdo sobre IA na Contabilidade
                    <>
                      {/* TL;DR - Resumo Executivo */}
                      <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-10 rounded-r-lg">
                        <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-flashlight-line text-teal-600"></i>
                          TL;DR — Resumo Executivo
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                          A IA na contabilidade está deixando de ser "chat para tirar dúvida" e virando <strong>camada operacional</strong>: ela lê documentos, classifica lançamentos, concilia extratos, sugere ações de fechamento, aponta anomalias e automatiza rotinas fiscais/financeiras. O "pulo do gato" é escolher ferramentas por processo (captura → classificação → conciliação → fechamento → governança), e não por hype. Este guia apresenta as principais ferramentas do mercado organizadas por categoria de uso prático.
                        </p>
                      </div>

                      {/* Seção 1 - Produtividade */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <i className="ri-lightbulb-line text-teal-600"></i>
                          1) IA para produtividade do time (texto, e-mails, planilhas, propostas e relatórios)
                        </h2>

                        {/* Microsoft 365 Copilot */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-microsoft-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Microsoft 365 Copilot</h3>
                              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-blue-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Escritório que vive em Excel/Outlook/Word/PowerPoint e quer acelerar relatórios, explicações para clientes e análises em planilhas.</p>
                              </div>
                              <div className="mb-4">
                                <p className="text-sm font-semibold text-gray-900 mb-2">💼 Use cases típicos:</p>
                                <ul className="space-y-2 ml-4">
                                  <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                                    <span>Explicar variações de DRE/BP em linguagem de cliente</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                                    <span>Criar relatórios gerenciais recorrentes</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                                    <span>Automatizar minuta de e-mail para cobrança/pendências</span>
                                  </li>
                                </ul>
                              </div>
                              <a href="https://www.microsoft.com/pt-br/microsoft-365/copilot" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Gemini for Google Workspace */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-google-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Gemini for Google Workspace / Gemini Enterprise</h3>
                              <div className="bg-purple-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-purple-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Escritórios com operação forte em Gmail/Docs/Sheets e colaboração em nuvem.</p>
                              </div>
                              <div className="mb-4">
                                <p className="text-sm font-semibold text-gray-900 mb-2">💼 Use cases típicos:</p>
                                <ul className="space-y-2 ml-4">
                                  <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                                    <span>Resumo de conversas com cliente (pendências)</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                                    <span>Rascunho de comunicados e políticas internas</span>
                                  </li>
                                  <li className="flex items-start gap-2 text-sm text-gray-700">
                                    <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                                    <span>Apoio a planilhas e documentação de processos</span>
                                  </li>
                                </ul>
                              </div>
                              <a href="https://workspace.google.com/solutions/ai/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Seção 2 - OCR/Extração */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <i className="ri-file-text-line text-teal-600"></i>
                          2) IA para entrada de documentos (OCR/extração de dados: notas, recibos, faturas)
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Essa é a categoria que mais dá <strong>ROI rápido</strong>: menos digitação e menos erro.
                        </p>

                        {/* Dext */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-file-upload-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Dext (ex-Receipt Bank)</h3>
                              <div className="bg-green-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-green-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Captura de comprovantes/recibos e organização de documentos para escritórios e clientes.</p>
                              </div>
                              <a href="https://dext.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* ABBYY FlexiCapture */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-indigo-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-scan-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">ABBYY FlexiCapture</h3>
                              <div className="bg-indigo-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-indigo-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Operação mais "corporate" e alto volume de documentos com necessidade de controle e precisão.</p>
                              </div>
                              <a href="https://www.abbyy.com/flexicapture/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Google Cloud Document AI */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-blue-500 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-cloud-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Google Cloud Document AI</h3>
                              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-blue-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Empresas/escritórios que querem pipeline customizado (API, integrações, escala).</p>
                              </div>
                              <a href="https://cloud.google.com/document-ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Rossum */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-robot-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Rossum</h3>
                              <div className="bg-purple-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-purple-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Extração "template-free" para faturas/contas a pagar com validação humana no loop.</p>
                              </div>
                              <a href="https://rossum.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* CPCON */}
                        <div className="bg-white border-2 border-teal-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-building-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">CPCON (Ativo Fixo)</h3>
                              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-teal-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Permite ter controle total do cadastro de ativos.</p>
                              </div>
                              <a href="https://grupocpcon.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Seção 3 - ERP/Financeiro */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <i className="ri-database-2-line text-teal-600"></i>
                          3) IA para ERP/financeiro com automações (PMEs) — contabilidade "conectada"
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Aqui, a IA entra para conciliar, categorizar, reduzir retrabalho e melhorar a rotina do cliente.
                        </p>

                        {/* Conta Azul */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-calculator-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Conta Azul – Conta AI</h3>
                              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-blue-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">PMEs com necessidade de financeiro organizado e integração com contador.</p>
                              </div>
                              <a href="https://contaazul.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Omie */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-line-chart-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Omie</h3>
                              <div className="bg-green-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-green-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Ecossistemas de ERP/financeiro em que o contador atua com o cliente (fiscal + financeiro).</p>
                              </div>
                              <a href="https://www.omie.com.br" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Nibo */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-exchange-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Nibo</h3>
                              <div className="bg-purple-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-purple-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Escritórios que querem padronizar o fluxo cliente→contador e acelerar conciliações e rotinas.</p>
                              </div>
                              <a href="https://nibo.com.br" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Seção 4 - Contas a Pagar */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <i className="ri-money-dollar-circle-line text-teal-600"></i>
                          4) IA para automação de contas a pagar (AP) e despesas (expense management)
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Ótimo para escritórios BPO/terceirização financeira e clientes com volume.
                        </p>

                        {/* Tipalti */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-indigo-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-wallet-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Tipalti – Finance AI/Agents</h3>
                              <div className="bg-indigo-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-indigo-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">AP robusto, compliance e automação ponta a ponte.</p>
                              </div>
                              <a href="https://tipalti.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* BILL */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-yellow-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-file-list-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">BILL – Accounts Payable + AI</h3>
                              <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-yellow-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Automação de faturas, captura de campos e roteamento de aprovações.</p>
                              </div>
                              <a href="https://www.bill.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Brex */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-red-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-bank-card-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Brex – Expense management</h3>
                              <div className="bg-red-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-red-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Gestão de despesas com políticas, categorização e automação do fluxo de gastos.</p>
                              </div>
                              <a href="https://www.brex.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Seção 5 - Conciliação e Fechamento */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <i className="ri-checkbox-multiple-line text-teal-600"></i>
                          5) IA para conciliação contábil, intercompany e fechamento (close management)
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          A dor aqui é: <strong>fechar rápido, com rastreabilidade e menos erro humano</strong>.
                        </p>

                        {/* BlackLine */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-gray-900 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-file-check-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">BlackLine</h3>
                              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-gray-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Médio/grande porte, conciliações e intercompany com automações e analytics.</p>
                              </div>
                              <a href="https://www.blackline.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* FloQast */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-calendar-check-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">FloQast – AI-powered Close</h3>
                              <div className="bg-teal-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-teal-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Times financeiros que precisam de organização do fechamento e status de tarefas, com apoio de IA.</p>
                              </div>
                              <a href="https://floqast.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Seção 6 - Auditoria e Anomalias */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <i className="ri-shield-check-line text-teal-600"></i>
                          6) IA para auditoria, detecção de anomalias e "red flags"
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Categoria excelente para <strong>serviços premium</strong>: revisão analítica, auditoria interna, prevenção a fraudes e melhorias de controles.
                        </p>

                        {/* MindBridge AI */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-brain-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">MindBridge AI</h3>
                              <div className="bg-purple-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-purple-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Análise de risco e detecção de transações anômalas em bases contábeis.</p>
                              </div>
                              <a href="https://www.mindbridge.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Caseware AiDA */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-search-eye-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Caseware AiDA</h3>
                              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-blue-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Firmas de auditoria/asseguração e projetos de analytics para auditoria.</p>
                              </div>
                              <a href="https://www.caseware.com/aida/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Seção 7 - Tributário e Compliance */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <i className="ri-scales-line text-teal-600"></i>
                          7) IA para tributário e compliance (pesquisa, classificação e risco)
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Aqui é o "cérebro" para <strong>reduzir risco e acelerar decisões</strong> (especialmente em operações complexas).
                        </p>

                        {/* Thomson Reuters */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-orange-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-book-open-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Thomson Reuters – AI powered tax & accounting research</h3>
                              <div className="bg-orange-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-orange-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Pesquisa tributária e respostas assistidas por IA em bases de conteúdo da própria TR.</p>
                              </div>
                              <a href="https://tax.thomsonreuters.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Sovos */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-global-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Sovos – Sovi AI</h3>
                              <div className="bg-green-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-green-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Compliance fiscal em escala (multipaís/alto volume), camada de inteligência para conformidade.</p>
                              </div>
                              <a href="https://sovos.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Wolters Kluwer */}
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-14 h-14 bg-blue-700 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-file-shield-line text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold text-gray-900 mb-3">Wolters Kluwer – CCH Axcess Intelligence</h3>
                              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-blue-900 mb-2">🎯 Onde brilha:</p>
                                <p className="text-sm text-gray-700">Ecossistemas tributários avançados com assistência de IA para profissionais de tax.</p>
                              </div>
                              <a href="https://www.wolterskluwer.com/en/solutions/cch-axcess" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm">
                                Saiba mais <i className="ri-external-link-line"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Seção 8 - RPA e Integrações */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                          <i className="ri-links-line text-teal-600"></i>
                          8) IA + RPA + integrações (conectar tudo e "rodar sozinho")
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Se o seu escritório quer virar máquina, isso é essencial. Você automatiza:<br />
                          <strong>entrada de documentos → OCR → validação → integração ERP → alertas → tarefas → atualização em planilha</strong>
                        </p>

                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-8">
                          <div className="flex items-start gap-4 mb-6">
                            <div className="w-16 h-16 bg-purple-600 text-white rounded-xl flex items-center justify-center flex-shrink-0">
                              <i className="ri-robot-2-line text-3xl"></i>
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900 mb-2">UiPath Document Understanding</h3>
                              <p className="text-gray-700">Plataforma líder em RPA com capacidades avançadas de processamento de documentos e automação de processos end-to-end.</p>
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4 mb-6">
                            <div className="bg-white p-4 rounded-lg">
                              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                <i className="ri-checkbox-circle-fill text-purple-600"></i>
                                Automação de processos
                              </h4>
                              <p className="text-sm text-gray-700">Conecta sistemas, extrai dados e executa tarefas sem intervenção humana</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                <i className="ri-checkbox-circle-fill text-purple-600"></i>
                                Inteligência de documentos
                              </h4>
                              <p className="text-sm text-gray-700">OCR avançado com IA para extrair dados de qualquer tipo de documento</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                <i className="ri-checkbox-circle-fill text-purple-600"></i>
                                Orquestração de fluxos
                              </h4>
                              <p className="text-sm text-gray-700">Coordena múltiplas ferramentas e sistemas em um único workflow</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg">
                              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                <i className="ri-checkbox-circle-fill text-purple-600"></i>
                                Escalabilidade
                              </h4>
                              <p className="text-sm text-gray-700">Processa milhares de documentos simultaneamente com alta precisão</p>
                            </div>
                          </div>
                          <a href="https://www.uipath.com/product/document-understanding" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold">
                            Saiba mais sobre UiPath <i className="ri-external-link-line"></i>
                          </a>
                        </div>
                      </section>

                      {/* Conclusão */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusão: escolha por processo, não por hype</h2>
                        <div className="bg-gradient-to-br from-teal-50 to-blue-50 border-2 border-teal-200 rounded-xl p-8">
                          <p className="text-gray-700 leading-relaxed mb-4">
                            A IA na contabilidade não é mais uma promessa futurista — é <strong>realidade operacional</strong>. As ferramentas existem, funcionam e entregam ROI mensurável quando bem implementadas.
                          </p>
                          <p className="text-gray-700 leading-relaxed mb-4">
                            O segredo está em <strong>mapear seus processos críticos</strong> (captura de documentos, classificação, conciliação, fechamento, auditoria, compliance) e escolher ferramentas específicas para cada etapa, em vez de buscar uma "solução mágica única".
                          </p>
                          <div className="bg-white p-6 rounded-lg mt-6">
                            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                              <i className="ri-lightbulb-line text-teal-600 text-xl"></i>
                              Próximos passos recomendados:
                            </h3>
                            <ul className="space-y-2">
                              <li className="flex items-start gap-2 text-gray-700">
                                <i className="ri-arrow-right-circle-fill text-teal-600 mt-1"></i>
                                <span><strong>Mapeie seus gargalos:</strong> Onde você perde mais tempo? Onde há mais erro?</span>
                              </li>
                              <li className="flex items-start gap-2 text-gray-700">
                                <i className="ri-arrow-right-circle-fill text-teal-600 mt-1"></i>
                                <span><strong>Comece pequeno:</strong> Escolha 1-2 processos para pilotar com IA</span>
                              </li>
                              <li className="flex items-start gap-2 text-gray-700">
                                <i className="ri-arrow-right-circle-fill text-teal-600 mt-1"></i>
                                <span><strong>Meça resultados:</strong> Tempo economizado, redução de erros, satisfação do cliente</span>
                              </li>
                              <li className="flex items-start gap-2 text-gray-700">
                                <i className="ri-arrow-right-circle-fill text-teal-600 mt-1"></i>
                                <span><strong>Escale gradualmente:</strong> Expanda para outros processos conforme ganha confiança</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </section>
                    </>
                  ) : article.slug === 'compliance-fiscal-evite-multas' ? (
                    // Novo conteúdo sobre Compliance Fiscal e Reforma Tributária
                    <>
                      {/* Introdução */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Introdução</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          A reforma tributária brasileira representa uma das maiores transformações do sistema fiscal das últimas décadas. Com a proposta de simplificação, unificação de tributos e mudanças profundas na forma de arrecadação, empresas de todos os portes enfrentam um novo cenário de riscos e oportunidades. Nesse contexto, o compliance fiscal deixa de ser apenas uma boa prática e passa a ser um <strong>fator estratégico essencial</strong> para a sobrevivência e a competitividade dos negócios.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                          Adotar uma postura preventiva, estruturada e alinhada às novas exigências legais é o caminho mais seguro para evitar multas, penalidades e passivos fiscais que podem comprometer seriamente a saúde financeira das organizações.
                        </p>
                      </section>

                      {/* O que é Compliance Fiscal */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">O que é Compliance Fiscal?</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Compliance fiscal é o conjunto de processos, controles internos, políticas e práticas adotadas por uma empresa para garantir o cumprimento integral da legislação tributária, em todas as esferas — federal, estadual e municipal.
                        </p>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Ele envolve, entre outros pontos:
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                          <li>Apuração correta de tributos</li>
                          <li>Cumprimento de obrigações acessórias</li>
                          <li>Entrega de declarações no prazo</li>
                          <li>Escrituração fiscal adequada</li>
                          <li>Acompanhamento constante da legislação</li>
                          <li>Gestão de riscos e contingências tributárias</li>
                        </ul>
                        <p className="text-gray-700 leading-relaxed">
                          Mais do que evitar problemas com o Fisco, o compliance fiscal promove <strong>transparência, previsibilidade e segurança jurídica</strong>.
                        </p>
                      </section>

                      {/* Reforma Tributária */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">A Reforma Tributária e os Novos Desafios para as Empresas</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          A reforma tributária traz mudanças estruturais, como:
                        </p>
                        <div className="grid md:grid-cols-2 gap-4 mb-6">
                          <div className="bg-teal-50 p-5 rounded-lg">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-exchange-line text-teal-600"></i>
                              Substituição de tributos
                            </h3>
                            <p className="text-sm text-gray-700">Implementação de impostos sobre valor agregado (IVA)</p>
                          </div>
                          <div className="bg-blue-50 p-5 rounded-lg">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-file-list-line text-blue-600"></i>
                              Novos tributos
                            </h3>
                            <p className="text-sm text-gray-700">Criação do CBS e do IBS</p>
                          </div>
                          <div className="bg-purple-50 p-5 rounded-lg">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-money-dollar-circle-line text-purple-600"></i>
                              Regras de crédito
                            </h3>
                            <p className="text-sm text-gray-700">Alterações nas regras de crédito tributário</p>
                          </div>
                          <div className="bg-orange-50 p-5 rounded-lg">
                            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <i className="ri-shield-check-line text-orange-600"></i>
                              Nova fiscalização
                            </h3>
                            <p className="text-sm text-gray-700">Novos modelos de fiscalização e arrecadação</p>
                          </div>
                        </div>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          Essas mudanças exigem das empresas:
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 ml-4">
                          <li>Revisão de processos fiscais</li>
                          <li>Adequação de sistemas e ERPs</li>
                          <li>Capacitação das equipes</li>
                          <li>Reavaliação de estratégias tributárias</li>
                        </ul>
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                          <p className="text-gray-700">
                            <strong>⚠️ Atenção:</strong> Empresas que não se prepararem correm o risco de cometer erros por desconhecimento ou má interpretação da nova legislação — o que pode resultar em autuações automáticas, multas elevadas e sanções administrativas.
                          </p>
                        </div>
                      </section>

                      {/* Principais Riscos */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Principais Riscos da Falta de Compliance Fiscal</h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          A ausência ou fragilidade do compliance fiscal pode gerar consequências graves:
                        </p>
                        <div className="space-y-4">
                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">💰 Multas e Autuações</h3>
                            <p className="text-gray-700 text-sm">Multas por atraso ou erro em declarações e autos de infração com fiscalizações recorrentes</p>
                          </div>
                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">📈 Juros e Encargos</h3>
                            <p className="text-gray-700 text-sm">Juros e encargos sobre tributos não recolhidos que aumentam rapidamente</p>
                          </div>
                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">🚫 Bloqueios Operacionais</h3>
                            <p className="text-gray-700 text-sm">Bloqueio de certidões negativas e impedimentos para participar de licitações</p>
                          </div>
                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">⚖️ Responsabilização</h3>
                            <p className="text-gray-700 text-sm">Responsabilização pessoal de sócios e administradores</p>
                          </div>
                          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <h3 className="font-bold text-gray-900 mb-2">🏢 Danos à Reputação</h3>
                            <p className="text-gray-700 text-sm">Prejuízos irreparáveis à imagem da empresa no mercado</p>
                          </div>
                        </div>
                        <p className="text-gray-700 leading-relaxed mt-6">
                          <strong>Em tempos de reforma tributária, esses riscos tendem a se intensificar</strong>, já que os órgãos fiscalizadores utilizam cada vez mais cruzamento eletrônico de dados e inteligência artificial.
                        </p>
                      </section>

                      {/* Como Implementar */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Como Implementar um Compliance Fiscal Eficiente</h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Para enfrentar o novo cenário tributário com segurança, algumas ações são fundamentais:
                        </p>

                        <div className="space-y-6">
                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-book-open-line text-xl"></i>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">1. Atualização Constante da Legislação</h3>
                                <p className="text-gray-700">Acompanhar leis, decretos, normas e interpretações fiscais é indispensável, especialmente durante o período de transição da reforma.</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-map-pin-line text-xl"></i>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">2. Mapeamento de Processos e Obrigações</h3>
                                <p className="text-gray-700">Identifique todos os tributos incidentes sobre as operações da empresa e as respectivas obrigações acessórias, evitando lacunas e retrabalho.</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-purple-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-refresh-line text-xl"></i>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">3. Revisão e Padronização de Rotinas</h3>
                                <p className="text-gray-700">Padronizar procedimentos reduz erros operacionais e garante consistência nas apurações e entregas.</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-green-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-robot-line text-xl"></i>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">4. Uso de Tecnologia e Automação</h3>
                                <p className="text-gray-700">Sistemas fiscais integrados e atualizados minimizam falhas humanas, facilitam o controle e aumentam a eficiência.</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-orange-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-search-eye-line text-xl"></i>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">5. Auditorias e Revisões Periódicas</h3>
                                <p className="text-gray-700">Auditorias internas ajudam a identificar inconsistências antes que se transformem em problemas com o Fisco.</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-indigo-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-graduation-cap-line text-xl"></i>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">6. Treinamento da Equipe</h3>
                                <p className="text-gray-700">Capacitar profissionais das áreas fiscal, contábil e financeira é essencial para interpretar corretamente as novas regras.</p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 bg-red-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-external-link-line text-xl"></i>
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">7. Apoio Especializado</h3>
                                <p className="text-gray-700">Contar com consultoria tributária especializada pode fazer toda a diferença na adaptação à reforma e na mitigação de riscos.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Vantagem Competitiva */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Compliance Fiscal como Vantagem Competitiva</h2>
                        <p className="text-gray-700 leading-relaxed mb-6">
                          Empresas que investem em compliance fiscal não apenas evitam penalidades, mas também:
                        </p>
                        <div className="bg-gradient-to-br from-teal-50 to-blue-50 border-2 border-teal-200 rounded-xl p-8">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="flex items-start gap-3">
                              <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-1">Previsibilidade Financeira</h3>
                                <p className="text-sm text-gray-700">Eliminam surpresas e planejam melhor o fluxo de caixa</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-1">Redução de Passivos</h3>
                                <p className="text-sm text-gray-700">Minimizam riscos de contingências ocultas</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-1">Governança Corporativa</h3>
                                <p className="text-sm text-gray-700">Fortalecem processos e controles internos</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-1">Confiança do Mercado</h3>
                                <p className="text-sm text-gray-700">Ganham credibilidade junto a investidores e parceiros</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-1">Decisões Estratégicas</h3>
                                <p className="text-sm text-gray-700">Baseiam escolhas em dados confiáveis e seguros</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                              <div>
                                <h3 className="font-bold text-gray-900 mb-1">Diferencial Competitivo</h3>
                                <p className="text-sm text-gray-700">Destacam-se em um mercado cada vez mais regulado</p>
                              </div>
                            </div>
                          </div>
                          <p className="text-gray-700 leading-relaxed mt-6 font-semibold">
                            Em um ambiente tributário mais transparente e integrado, estar em conformidade deixa de ser um custo e se torna um <strong>diferencial competitivo</strong>.
                          </p>
                        </div>
                      </section>

                      {/* Conclusão */}
                      <section className="mb-10">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Conclusão</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                          A reforma tributária inaugura uma nova era para a gestão fiscal no Brasil. Diante desse cenário, o compliance fiscal se consolida como um <strong>pilar estratégico</strong> para empresas que desejam crescer de forma sustentável, segura e alinhada à legislação.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                          Antecipar-se às mudanças, investir em processos, tecnologia e conhecimento é a melhor forma de evitar multas, penalidades e garantir a perenidade do negócio em um ambiente cada vez mais desafiador.
                        </p>
                      </section>
                    </>
                  ) : (
                    // Conteúdo padrão para outros artigos
                    <div className="space-y-6 text-gray-700 leading-relaxed">
                      <p>
                        Este é um artigo completo sobre <strong>{article.title}</strong>. 
                        O conteúdo detalhado está sendo desenvolvido por nossos especialistas.
                      </p>
                      <p>
                        {article.excerpt}
                      </p>
                      <p>
                        Para mais informações sobre este tema, entre em contato com nossa equipe de especialistas.
                      </p>
                    </div>
                  )}
                </div>

                {/* Share Section */}
                <div className="mt-12 pt-8 border-t-2 border-gray-200">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-[#0F172A] mb-2">Gostou deste conteúdo?</h3>
                      <p className="text-sm text-[#64748B]">Compartilhe com seus colegas</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button className="w-10 h-10 flex items-center justify-center bg-[#1877F2] text-white rounded-lg hover:opacity-90 transition-opacity">
                        <i className="ri-facebook-fill text-xl"></i>
                      </button>
                      <button className="w-10 h-10 flex items-center justify-center bg-[#1DA1F2] text-white rounded-lg hover:opacity-90 transition-opacity">
                        <i className="ri-twitter-fill text-xl"></i>
                      </button>
                      <button className="w-10 h-10 flex items-center justify-center bg-[#0A66C2] text-white rounded-lg hover:opacity-90 transition-opacity">
                        <i className="ri-linkedin-fill text-xl"></i>
                      </button>
                      <button className="w-10 h-10 flex items-center justify-center bg-[#25D366] text-white rounded-lg hover:opacity-90 transition-opacity">
                        <i className="ri-whatsapp-fill text-xl"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </article>

              {/* Sidebar - 4 columns */}
              <aside className="lg:col-span-4">
                <div className="sticky top-24 space-y-8">
                  
                  {/* Author Card */}
                  <div className="bg-[#F8FAFC] rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                        {article.author.avatar ? (
                          <img src={article.author.avatar} alt={article.author.name} className="w-full h-full object-cover" />
                        ) : (
                          <i className="ri-user-line text-3xl text-gray-400"></i>
                        )}
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0F172A]">{article.author.name}</h3>
                        <p className="text-sm text-[#64748B]">{article.author.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#475569] mb-4">
                      Especialista em gestão de ativos e compliance contábil com mais de 10 anos de experiência.
                    </p>
                    <button className="w-full px-4 py-2 bg-[#1E40AF] text-white rounded-lg font-semibold hover:bg-[#1E3A8A] transition-colors">
                      Ver mais artigos
                    </button>
                  </div>

                  {/* Related Articles */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
                      <i className="ri-article-line text-[#1E40AF]"></i>
                      Artigos Relacionados
                    </h3>
                    <div className="space-y-4">
                      {allPosts.slice(0, 3).map((post) => (
                        <Link 
                          key={post.id} 
                          to={`/artigo/${post.slug}`}
                          className="block group"
                        >
                          <div className="flex gap-3">
                            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                              <img 
                                src={post.image} 
                                alt={post.title}
                                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                              />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm font-bold text-[#0F172A] leading-tight mb-1 group-hover:text-[#1E40AF] transition-colors line-clamp-2">
                                {post.title}
                              </h4>
                              <p className="text-xs text-[#64748B]">
                                {new Date(post.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Newsletter */}
                  <div className="bg-gradient-to-br from-[#1E40AF] to-[#1E3A8A] rounded-xl p-6 text-white">
                    <i className="ri-mail-line text-3xl mb-3"></i>
                    <h3 className="text-lg font-bold mb-2">Newsletter</h3>
                    <p className="text-sm text-white/90 mb-4">
                      Receba conteúdos exclusivos sobre gestão contábil
                    </p>
                    <input 
                      type="email" 
                      placeholder="Seu e-mail"
                      className="w-full px-4 py-2 rounded-lg text-[#0F172A] mb-3 text-sm"
                    />
                    <button className="w-full px-4 py-2 bg-white text-[#1E40AF] rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                      Inscrever-se
                    </button>
                  </div>

                </div>
              </aside>

            </div>
          </div>
        </div>

        {/* Feedback Banner - Full Width */}
        <div className="w-full bg-white">
          <FeedbackBanner 
            articleTitle={article.title}
            articleUrl={typeof window !== 'undefined' ? window.location.href : ''}
          />
        </div>

      </div>
      
      <Footer />
    </>
  );
}
